import { isPayrollStatusReviewer } from '@/lib/payroll/coordinator-status';
import {
  AGENCY_ATTESTATION_UI_ROLES,
  AGENCY_IN_CHARGE_UI_ROLES,
  AGENCY_REVIEW_UI_ROLES,
} from '@/lib/agency/agency-help-roles';
import { AGENCY_INVOICE_ALLOWED_ROLES } from '@/lib/payroll/agency-invoice-access';
import { CARE_NOTE_EXPORT_ROLES } from '../care-notes/care-note-period-export';
import { B2G_VIEW_ROLES, B2G_SUBMIT_ROLES } from '../integrations/government/b2g-access';
import { STAFF_PROFILE_EDIT_ROLES } from '../hr/staff-profile-edit-roles';
import {
  INVOICING_LINK_ROLES,
  PAYROLL_LINK_ROLES,
} from '../integrations/accounting/accounting-link';
import { REPORT_SCHEDULE_MANAGEMENT_ROLES } from '../dashboard-reports/calendar-review-types';
import { FRONTLINE_WOUND_ALLOWED_ROLES } from '../capabilities';
import {
  RECRUITMENT_SCOPED_ROLES,
  RECRUITMENT_TENANT_WIDE_ROLES,
} from '../workforce/recruitment-capabilities';
import type { ModuleOrgType } from '../billing';
import type { ProviderType } from '../navigation/nav-v2';
import type { ProviderProgramContext } from '../navigation/provider-type';
import { hasAnyRole } from '../rbac';
import { normalizeHelpChatPath } from './help-path';
import { isModuleNavigationVisible } from '../module-access';
import { isHrefVisibleForProviderProgram } from '../navigation/program-route-policy';
import { rewriteMvpPolishL1Href } from '../navigation/mvp-polish-l1-allowlist';
import { matchGeneratedProgramRoutePolicy } from '@statura/experience-contract';
import type { GovernmentIntegrationEnablement } from '../integrations/government/enablement-types';
import type { HelpProgramTag } from './types';
import {
  DHDA_INTEGRATION_ROLES,
  DHDA_INTEGRATION_SETUP_ROLES,
  GOVERNMENT_PROVIDER_SETUP_CONFIG_ROLES,
  SERVICES_AUSTRALIA_INTEGRATION_ROLES,
} from '../integrations/government/access';

export type HelpChatRoleCohort = 'coordinator' | 'care_worker' | 'clinician' | 'other';

/** Explicit tenant flag only; absent/loading/error contexts keep legacy ordering. */
export type HelpSuggestionFeatures = {
  unifiedIncidentCapture?: boolean;
  payrollRebuildOn?: boolean;
  /** care_today_v1 for the tenant; absent, loading or failed keeps the baseline suggestions. */
  careToday?: boolean;
  stationPinManagerRevoke?: boolean;
  stationPinSelfService?: boolean;
  agencyAccess?: boolean;
};

// care_today_v1 suggestions. They are offered only when the tenant flag is
// explicitly ON, after the existing path, role, module and route gates admitted
// the anchor question for the same page. Answer owners: wound-management
// (explicit measurement evidence), workforce-handovers (client handover in the
// care worker app) and client-record-tabs (Care Today orientation).
const CARE_TODAY_PATH_SUGGESTIONS: ReadonlyArray<{
  prefix: string;
  question: string;
  before: string;
}> = [
  {
    prefix: '/clinical/wounds',
    question: 'How do I record that a wound could not be measured?',
    before: 'How do I record a wound assessment during a clock change?',
  },
  {
    prefix: '/clinical/handovers',
    question: 'How do I record a client handover in home care?',
    before: 'How do I find the right client in a shared picker?',
  },
  {
    prefix: '/clients',
    question: 'How do I use Care Today on a client record?',
    before: 'Why is Support at Home information unavailable after changing programme?',
  },
];

function withCareTodaySuggestions(
  questions: string[],
  prefix: string | undefined,
  features: HelpSuggestionFeatures | undefined,
): string[] {
  if (features?.careToday !== true || !prefix) return questions;
  const result = [...questions];
  for (const entry of CARE_TODAY_PATH_SUGGESTIONS) {
    if (entry.prefix !== prefix || result.includes(entry.question)) continue;
    const anchor = result.indexOf(entry.before);
    if (anchor >= 0) result.splice(anchor, 0, entry.question);
  }
  return result;
}

// Answer owner: incident-one-door-training. Include optional awareness, office
// confirmation/linking/death follow-up and the accountable urgent-cohort fallback.
const ONE_DOOR_QUESTIONS = [
  'How do I report care and workplace incidents through one form?',
  'What does saved, pending or not required mean on my incident receipt?',
] as const;
const ONE_DOOR_OFFICE_QUESTIONS = [
  'How do I finish reports from the one-door form?',
  'How do I reopen a dismissed pending report?',
] as const;

const COMMON_SUGGESTIONS = [
  'How do I approve timesheets?', // Answer owner: timesheets-and-leave; Log time and Timesheet tools.
  'How do I report a SIRS incident?',
] as const;

function isSuggestionModuleVisible(question: string, enabledModules?: string[] | null): boolean {
  let requiredPath: string | undefined;
  if (/SIRS incident/iu.test(question)) requiredPath = '/compliance/incidents';
  else if (/timesheets?/iu.test(question)) requiredPath = '/roster/timesheets';
  else if (/Xero/iu.test(question)) requiredPath = '/settings/integrations/xero';
  else if (/Support at Home|SAH/iu.test(question)) requiredPath = '/home-care';
  else if (/care minutes|accommodation charges/iu.test(question)) requiredPath = '/residential';
  // Same semantics as route entitlement: null/undefined modules mean unrestricted.
  return !requiredPath || isModuleNavigationVisible(requiredPath, enabledModules);
}

const HOME_CARE_SUGGESTIONS = [
  'How do I manage Support at Home budgets?',
  'How do I prepare Support at Home claims?',
] as const;

const RESIDENTIAL_SUGGESTIONS = [
  'How do I track care minutes?',
  'How do I manage resident accommodation charges?',
] as const;

const CARE_WORKER_ROSTER_QUESTIONS = [
  'How do I view my roster?',
  'How do I clock in to an assigned shift?',
  'What break is used when I clock out in the worker app?',
  'How do I submit my timesheet?',
  'How do I request leave?',
  'Why is my roster empty?',
] as const;

const CLINICIAN_ROSTER_QUESTIONS = [
  'How do I view my roster?',
  'How do I report a SIRS incident?',
  'How do I record a medication administration?',
] as const;

type ConditionalQuestions = {
  questions: readonly string[];
  requiresIntegration: keyof GovernmentIntegrationEnablement;
  requiresRoles: readonly string[];
  requiredModules: readonly string[];
};

type PathSuggestion = {
  roleQuestions?: readonly { questions: readonly string[]; requiresRoles: readonly string[] }[];
  prefix: string;
  questions: readonly string[];
  chspQuestions?: readonly string[];
  conditionalQuestionsLast?: boolean;
  byRole?: Partial<Record<HelpChatRoleCohort, readonly string[]>>;
  conditionalQuestions?: readonly ConditionalQuestions[];
  requiredModules?: readonly string[];
  requiresIntegration?: keyof GovernmentIntegrationEnablement;
  requiresRoles?: readonly string[];
  byProgram?: Partial<Record<ProviderType, readonly string[]>>;
};

const RESIDENT_REGISTRATION_PROGRAM_QUESTION =
  'Why does registering a resident say residential care isn’t switched on?';

export const PATH_SUGGESTIONS: readonly PathSuggestion[] = [
  {
    // Answer owner: support-assistant-budget; superadmin capability is enforced by the page.
    prefix: '/support-assistant-budget',
    requiresRoles: ['SYSTEM_ADMIN'],
    questions: ['How do I set the support assistant budget and confirm live prices?'],
  },
  {
    // Answer owner: palliative-comfort-follow-up; concern section includes
    // worker unknown-status Retry and cached-ON offline concern recovery.
    prefix: '/clients/comfort',
    questions: [
      'How do I record a comfort concern and confirm clinical acceptance?',
      'How do I check comfort supplies and hand over to the next clinician?',
      'How do I record a staff-led family comfort update?',
    ],
  },
  {
    prefix: '/settings/comfort-coordination',
    questions: ['How do I configure comfort cover or disable automation?'],
  },
  // Answer owner: s8-controlled-substance-register.
  {
    prefix: '/medications/s8-register',
    questions: [
      'How do I initialize S8 stock before the first receipt?',
      'What should I do when an S8 stock save has an uncertain outcome?',
      'Can I find ceased medication orders in the S8 stock form?',
      'How do I verify the witness for an S8 stock movement?',
    ],
  },
  {
    prefix: '/admin/system/mobile-devices/shared-stations',
    requiresRoles: ['SYSTEM_ADMIN', 'TENANT_ADMIN'],
    questions: [
      'How do I register and pair a shared station?',
      'What should I do when station pairing confirmation is pending?',
      'How does sign-in work on a clinical station?',
      'How do staff return with a station PIN and their own phone?',
      'How do I record fluid intake and check an uncertain save on a station?',
      'How do staff use team rounds and handover on a clinical station?',
      'What should I do when a team action is refused or confirmation is pending?',
      'How do I restore my unfinished form after a station locks?',
      'How do I prepare a scheduled medicine and recover an interrupted outcome?',
      'How do I transport retained medication evidence for clinical review?',
      'How do I clock in or out on a station without my phone?',
      'Does pairing activate a station or sign in a worker?',
      'How do I verify management, activate or replace a station setup?',
      'How do I pause or retire shared station access?',
      'How do I review attendance break policy and unresolved breaks?',
    ],
  },
  {
    prefix: '/admin/system/mobile-devices',
    requiresRoles: ['SYSTEM_ADMIN', 'TENANT_ADMIN'],
    questions: [
      'How do I register and pair a shared station?',
      'Does pairing activate a station or sign in a worker?',
      'How do I verify management, activate or replace a station setup?',
      'How do I pause or retire shared station access?',
      'How do I review attendance break policy and unresolved breaks?',
    ],
  },
  {
    prefix: '/home-care/assessments',
    questions: [
      'What does Finding awaiting clinical review mean for a home visit?',
      'Why are pending findings unavailable when clinical alerts are shown?',
    ],
  },
  {
    prefix: '/home-care/service-areas',
    questions: [
      'Why can’t staff deliver Support at Home visits in an active service area?',
      'How do I review a service-area access change after switching organisations?',
    ],
  },
  {
    prefix: '/payroll-switch',
    requiresRoles: ['SYSTEM_ADMIN'],
    questions: ['On /payroll-switch, how do I preview and confirm provider payroll switches?'],
  },
  {
    prefix: '/tenants',
    requiresRoles: ['SYSTEM_ADMIN'],
    questions: [
      'I am in the platform Organisations register. How do I provision and activate a tenant?',
      'Where do I select the organisation state when creating it?',
      'What should I do when organisation setup is incomplete?',
      'Does tenant activation release the communications hold?',
      'Why do organisation user counts exclude Statura Automation?',
    ],
  },
  {
    prefix: '/admin/system/master-data',
    requiresRoles: ['SYSTEM_ADMIN'],
    questions: [
      'How do I manage government rates and indexation?',
      'How do I reset exhausted government rate entry attempts?',
    ],
  },
  {
    prefix: '/clinical/wounds',
    questions: [
      'How do I record a wound assessment during a clock change?',
      'How do I respond to a wound deterioration notification?',
    ],
    requiresRoles: FRONTLINE_WOUND_ALLOWED_ROLES,
  },
  {
    prefix: '/reports',
    questions: [
      'Why does an old report address show Page not found?',
      'How do I download a generated report?',
      'Can I export an XLSX report whose title contains punctuation?',
    ],
  },
  {
    prefix: '/reports/subscriptions',
    questions: ['How do I review a dashboard schedule timezone?'],
    requiresRoles: REPORT_SCHEDULE_MANAGEMENT_ROLES,
  },
  {
    prefix: '/reports/employee_day_timesheets',
    questions: [
      'I am on /reports/employee_day_timesheets. How do I export hours by employee and day for an audit?',
    ],
  },
  {
    prefix: '/reports/qfr_payroll_evidence',
    questions: [
      'How do I prepare payroll hours and costs for QI or QFR?',
      'I am on /reports/qfr_payroll_evidence. How do I customise and save my report settings?',
      'I am on /reports/qfr_payroll_evidence. How do I prepare the previous month and check for missing information?',
      'I am on /reports/qfr_payroll_evidence. How do I add departmental, training or superannuation reconciliation evidence?',
      'I am on /reports/qfr_payroll_evidence. Why does this report say paid figures are pending?',
      'I am on /reports/qfr_payroll_evidence. Does Statura keep my reconciliation file?',
    ],
  },
  {
    prefix: '/reports/payroll_labour_cost',
    questions: [
      'I am on /reports/payroll_labour_cost. Why does this report say paid figures are pending?',
    ],
  },
  {
    prefix: '/reports/payroll_penalty_mix',
    questions: [
      'I am on /reports/payroll_penalty_mix. Why does this report say paid figures are pending?',
    ],
  },
  {
    prefix: '/reports/payroll_cost_per_hour',
    questions: [
      'I am on /reports/payroll_cost_per_hour. Why does this report say paid figures are pending?',
    ],
  },
  {
    prefix: '/reports/workforce_benchmark_indicators',
    questions: [
      'How do I get the monthly MOA workforce figures?',
      'Why are some hours under Not classified?',
    ],
  },
  {
    prefix: '/reports/qi_workforce_turnover',
    questions: [
      'How do I get the quarterly QI workforce figures?',
      'Why is a service manager missing from the QI workforce figures?',
    ],
  },
  {
    prefix: '/reports/policy_acknowledgement_status',
    questions: ['Who counts in policy acknowledgement reports?'],
  },
  {
    prefix: '/reports/incident_register',
    questions: [
      'How do I see all incidents for the month in one list?',
      'Why does an incident say Programme not recorded?',
    ],
  },
  {
    prefix: '/dashboards',
    questions: ['How do dashboard time ranges and Custom dates work?'],
  },
  {
    prefix: '/dashboards/don-clinical',
    questions: ['How are care-minute targets graded on the DON dashboard?'],
  },
  {
    prefix: '/dashboards/executive',
    questions: ['Why is the executive care-minute target grade unknown?'],
  },
  {
    prefix: '/people/compliance/obligations',
    questions: ['How do I return from Workforce obligations to worker compliance?'],
  },
  {
    prefix: '/people/learning/analytics',
    questions: [
      'Who counts in learning analytics and exports?',
      'Why are Learning analytics totals incomplete?',
      'How do I identify learning courses with the same title?',
    ],
  },
  {
    prefix: '/home-care/reconciliation',
    questions: [
      'Why are reconciliation totals unavailable until I choose a valid date range?',
      'Can participant-contribution invoices be used for Services Australia invoicing?',
    ],
  },
  {
    prefix: '/finance/reports/aged-debtors',
    questions: ['How are participant invoices and government claims aged?'],
  },
  {
    prefix: '/roster/runs',
    questions: [
      'How do I use the date picker, Previous day, Next day and Today in visit runs?',
      'What do travel unknown, overlaps and unassigned visits mean in Visit runs?',
      'How do I reset the client, area and period scope in Visit runs?',
      'How do I use the quiet roster header and controls?',
    ],
  },
  {
    prefix: '/workflows/instances',
    questions: [
      'How do I start a workflow from the run register?',
      'What should I do when a workflow has no steps?',
    ],
  },
  {
    prefix: '/settings/facility-config',
    questions: [
      'How do I record an S8 dose when the app cannot witness offline?',
      'Who can change the S8 phone-order provider setting?',
      'Who can require an administrator PIN for every dose?',
    ],
  },
  {
    prefix: '/settings/workflows/closeout',
    questions: ['What does workflow design coverage tell me?'],
  },
  {
    prefix: '/compliance/documents/expiring',
    questions: [
      'I am on /compliance/documents/expiring. Should I acknowledge, renew or replace a document?',
    ],
  },
  {
    prefix: '/compliance/audit-trail',
    questions: ['How does Actions Today count audit activity?'],
  },
  {
    prefix: '/activity',
    questions: ['Why does Activity Feed say field values are not available?'],
  },
  {
    prefix: '/compliance/documents',
    questions: [
      'I am on /compliance/documents. How do I browse documents and use folders?',
      'I am on /compliance/documents. How do I share a document with the family portal?',

      'What should I do if an expiring document update cannot be confirmed?',
      'How do I recover when document search has no matches?',
      'How do I upload, restore or review family documents?',
      'Can a team leader accept a family upload without filing it?',
      'How do I resolve a missing file or hash?',
      'How do I resolve a missing file or fingerprint?',
      'Why can I not open a document waiting for a virus check?',
      'Why was my document upload refused?',
    ],
  },
  {
    prefix: '/compliance/whs/equipment',
    questions: [
      'How do I record inspection evidence and follow up a failed test?',
      'What should I do if an equipment status update cannot be confirmed?',
      'How do equipment condition and inspection status differ?',
    ],
  },
  {
    prefix: '/compliance/whs/hazards',
    questions: [
      'How do I review whether a prevention action worked?',
      'Which prevention actions need a review?',
      'Why is a prevention evidence document unavailable?',
      'What should I do when a hazard status change is unconfirmed?',
      'How do I choose who is assigned to a hazard?',
      'How do I edit an open hazard?',
      'Does pressing Enter submit a hazard form?',
      'Where do I find a hazard reference number?',
    ],
  },
  {
    prefix: '/admin/system',
    requiresRoles: ['OWNER', 'TENANT_ADMIN'],
    questions: ['How are system administration badges labelled?'],
  },
  {
    prefix: '/compliance/responsible-persons',
    questions: [
      'I am on /compliance/responsible-persons. When does the Commissioner notification clock start?',
      'I am on /compliance/responsible-persons. How do I record or correct provider awareness?',
      'I am on /compliance/responsible-persons. How are historical suitability answers preserved?',
      'I am on /compliance/responsible-persons. Why is an appointment or cessation notice deadline labelled as an estimate?',
      'How are responsible person response badges labelled?',
    ],
  },
  {
    prefix: '/settings/signature-templates',
    questions: [
      'How are signature request status badges labelled?',
      'How do I manage signature templates, defaults and required signers?',
      'How do email codes protect external signing?',
      'Why could my signing code not be emailed?',
      'Why can I not issue a signing link?',
      'Can I create a public signing link for consent?',
      'How do I configure signing expiry, reminders and signature mode?',
      'I am on /settings/signature-templates. Can I customise an offer letter?',
      'I am on /settings/signature-templates. Who can open a saved version?',
      'I am on /settings/signature-templates. Why is my Settings save refused after access changes?',
    ],
  },
  { prefix: '/monitoring', questions: ['How are monitoring badge labels displayed?'] },
  {
    prefix: '/settings/data-management',
    questions: [
      'What happens if a deletion or anonymisation request is held?',
      'I am on /settings/data-management. Where can I check virus scanning status?',
      'Where can I check virus scanning status?',
      'Why was my document upload refused?',
      'Why does a data request control say Ask your organisation administrator?',
    ],
  },
  {
    prefix: '/people/learning',
    questions: [
      'I am on /people/learning. How do I waive a download module with no file?',
      'How do I search learning assignments by full name and clear filters?',
      'How do I open a selected learning assignment outside the current page?',
      'I am on /people/learning. How do I preview a course as a worker without assigning it?',
      'I am on /people/learning. What can HR manage and export in Learning?',
      'I am on /people/learning. What can HR read in Learning, and why is CSV export unavailable?',
    ],
  },
  {
    prefix: '/settings/document-templates',
    questions: [
      'I am on /settings/document-templates. How do I choose which document types care workers see?',
      'How do I choose which document types care workers see?',
      'I am on /settings/document-templates. How do I preview the current workforce document branding?',
      'I am on /settings/document-templates. How do I revise, clone, preview or archive a workforce document template?',
      'I am on /settings/document-templates. Why is my Settings save refused after access changes?',
      'Why does saving a document type say Ask your organisation administrator?',
    ],
  },
  {
    // Covers immutable consent evidence, verified approval and the recent-approval detective list.
    prefix: '/admin/system/signature-corrections',
    requiresRoles: ['SYSTEM_ADMIN', 'TENANT_ADMIN'],
    questions: ['How do I correct a signing record without changing original evidence?'],
  },
  // The answer includes durable decline delivery and temporary-failure retries.
  { prefix: '/sign', questions: ['What should I do when a public signing link is unavailable?'] },
  {
    prefix: '/settings/retention',
    questions: [
      'What does Rehearse pending archives check?',
      'How do I extend retention or set document reminders?',
      'Does Retention delete records?',
      'What does would archive mean?',
      'Why does saving retention or reminders say Ask your organisation administrator?',
    ],
  },
  {
    prefix: '/finance/contributions',
    questions: ['Who can open Finance > Contributions?'],
    chspQuestions: [
      'How do I record a CHSP contribution policy and client agreement?',
      'Why has a captured CHSP service not created a contribution event?',
      'How do I prepare a CHSP contribution invoice?',
      'How do CHSP charging caps, posting dates and closed periods affect contributions?',
      'Can a finance officer manage CHSP contributions without a care assignment?',
      'Why is a queued CHSP credit paused when contributions are switched off?',
    ],
  },
  {
    prefix: '/home-care/schedules',
    questions: [
      'How do I edit one occurrence, this and following, or a whole schedule series?',
      'How do I create repeating home-care visits without a care plan?',
      'How do I link a roster-authored series to a care plan?',
      'How do I repeat care-only visits and check worker attendance awaiting confirmation?',
      'Which visits are protected when I edit a schedule series?',
      'Where do I adjust recurring schedule times from the client Delivery tab?',
      'Where are recurring schedules?',
      'What if I need to swap a worker for one visit?',
      'How do I review the next two weeks?',
      'How do I use the recurring client grid and worker lens?',
      'How do I generate visits and keep context when allocating runs?',
      'Why do funding, budget and inclusion labels not stop scheduling?',
      'How do I use the quiet roster header and controls?',
      'How do I roster a split visit for a client without a care plan?',
    ],
  },
  {
    prefix: '/roster/open-shifts',
    questions: [
      'Why is an open-shift broadcast closed after I assign a worker?',
      'Where are Open shifts and Handovers in Cover & attendance?',
      'How do I dispatch an unfilled shift?',
      'Where do I find the five roster destinations?',
      'How do I use the quiet roster header and controls?',
      'How do I review shift offers and find unfilled shifts?',
    ],
  },
  {
    prefix: '/roster/periods/new',
    questions: [
      'How do I choose a roster period facility?',
      'When should I choose Organisation-wide / shared for a roster period?',
      'How do I use the quiet roster header and controls?',
    ],
  },
  {
    prefix: '/roster/periods',
    questions: [
      'How do I publish or unpublish a roster service line as a facility manager?',
      'How do I choose a roster period facility?',
      'How do period scope and wing filters affect Generate shifts and Dispatch links?',
      'Where do I find the five roster destinations?',
      'How do I recover when roster publication is refused?',
      'Why do roster checks need refreshing after a change?',
      // New workflows retain the established six visible questions.
      'On /roster/periods, how do I prepare upcoming public holiday shifts?',
      // New questions append after the established first six; they never displace them.
      'Where do I see a roster period facility and programme?',
      'Who can re-run checks when roster publication is refused?',
      'How do I apply a Master roster from a draft period?',
      'I am on /roster/periods. How do I use Review & publish and Publish changes?',
      'How do I review delivery evidence, failed roster notices and confirmed family visit updates?',
      'I am on /roster/periods. How do I clear blocked shifts and refill them after publishing?',
      'I am on /roster/periods. What must I do after clearing blocked shifts?',
      'I am on /roster/periods. Where do I review closeout readiness and correct locked or paid history?',
      'I am on /roster/periods. Where does More open period details, evidence and the audit trail?',
      'I am on /roster/periods. How do I use Filters for scope and status, Checks, Retry publication checks and More for columns and density?', // Includes every severity and unknown-state distinction.
      'How do I use the quiet roster header and controls?',
      'Why is my roster read-only?',
      'How do I keep scheduling a legacy mixed period?',
      'How do I cancel a draft roster period I no longer need?', // roster-management: Cancel draft period.
      'How do I publish visits added to a published or early-locked roster period?',
      'How do I open a finding on the roster or review a missing forecast?',
      'Why does a planless visit need a funding review?',
    ],
  },
  {
    prefix: '/roster/health/financial-setup',
    questions: ['How do I review roster health financial setup holds?'],
  },
  {
    prefix: '/roster/health',
    questions: [
      'Can I roster home-care clients with different funding programmes together?',
      'How do I review roster health financial setup holds?',
      'How do I resolve an orphaned schedule entry?',
      'How do I use the quiet roster header and controls?',
    ],
  },
  {
    prefix: '/roster/optimiser',
    questions: [
      'How do I review and apply selected roster suggestions?',
      'What does Latest application mean in roster suggestions?',
      'Why do scheduling suggestions show Not assessed?',
      'How do I use the quiet roster header and controls?',
    ],
  },
  // Roster Quiet Ledger lane: secondary register presentation help.
  {
    prefix: '/workforce/capabilities',
    questions: [
      'How do I use the Worker skills & certifications registers?', // workforce-capabilities: Quiet Ledger presentation.
      'How do I use the quiet roster header and controls?',
    ],
  },
  {
    prefix: '/workforce/mobile-activity',
    questions: [
      'What does Worker app activity show?', // workforce-capabilities: Quiet Ledger presentation.
      'How do I use the quiet roster header and controls?',
    ],
  },
  {
    prefix: '/workforce/timesheets/disputes',
    questions: ["How do I review and resolve a worker's time or pay query?"],
  },
  {
    prefix: '/workforce/availability',
    questions: [
      'How do I find workers and page through availability?',
      'What happens to the availability page when records expire?',
      'How do I view exception history?',
      'How do I use the Team availability register?', // workforce-availability: Quiet Ledger presentation.
      'How do I use the quiet roster header and controls?',
      'How do I remove a repeating availability pattern?', // workforce-availability: Remove on Repeating patterns.
    ],
  },
  {
    prefix: '/incidents',
    questions: [
      'How do I report a portal incident without knowing the programme?',
      'How do I retry the same portal incident after switching programmes?',
    ],
  },
  {
    prefix: '/compliance/incidents/new',
    questions: [
      'Why does a new care incident ask who was affected first?',
      'How do I record a missed dose or another medication incident type?',
      'How do I record whether a fall was witnessed?',
      'What should I do about an unexplained injury?',
      'Can I save an incident before confirming its programme?',
      'How do I retry a mobile incident awaiting programme authority?',
      'Do I need to know the incident type and severity before I can save a report?',
      'How do I report a portal incident without knowing the programme?',
      'How do I enter an incident from our old system?',
      'How do I record a staff injury from a care incident?',
      'Why does the incident suggest a type from the complaint?',
      'How do I report care and workplace incidents through one form?',
      'What does saved, pending or not required mean on my incident receipt?',
      'Why is Use the standard form hidden while my incident receipt is uncertain?',
      'Where is source-person follow-up managed after a needle-stick?',
      'Which form opens when I report from the care incident register?',
      'How do I record an agency worker or actions to prevent a repeat?',
      'Where do I explain an Other medication incident type?',
      'How do I record a Medical episode and descriptive additional types?',
    ],
  },
  {
    prefix: '/compliance/restrictive-practices/new',
    questions: ['Can I save a restrictive practice before confirming its programme?'],
  },
  {
    prefix: '/compliance/complaints/new',
    questions: ['Can I save a complaint before confirming its programme?'],
  },
  {
    prefix: '/compliance/ipc/surveillance/new',
    questions: ['Can I save an infection before confirming its programme?'],
  },
  {
    prefix: '/compliance/ndis-overlay',
    questions: [
      'How do I assess an NDIS incident and record a manual submission receipt?',
      'How do I add a follow-up or final report to an earlier NDIS obligation?',
      'How do I activate an NDIS registration plan draft?',
    ],
  },
  {
    prefix: '/settings/onboarding/worker/base',
    questions: ['What if onboarding settings could not be loaded?'],
  },
  {
    prefix: '/people/learning/courses',
    questions: [
      'I am on /people/learning/courses. Which lesson content types work?',
      'I am on /people/learning/courses. What should I check before archiving the whistleblower course?',
      'What changes when I archive a course?',
      'Does marking a course mandatory assign it to new hires?',
      'Can I change lessons in a published course?',
      'Can a learning manager preview a video without a course assignment?',
    ],
  },
  {
    prefix: '/settings/onboarding/screening',
    questions: [
      'How do I set screening requirements for a role and facility?',
      'How do I record an NDIS matrix risk-role assessment and follow-up task?',
      'How do I set screening requirements for compliance-only workers?',
      'How do I require a second person to review an adverse screening decision?',
    ],
  },
  {
    prefix: '/people/policies-conduct',
    questions: [
      'How do I draft, publish and assign the provider-approved NDIS Code of Conduct version?',
      'I am on /people/policies-conduct. How do I preview the worker acknowledgement?',
      'I am on /people/policies-conduct. How do I choose a material or minor policy change?',
      'I am on /people/policies-conduct/publications. Why must some workers acknowledge again after a minor change?',
      'I am on /people/policies-conduct. Can managers author and publish policies, see all drafts and add audited comments?',
      'How do HR roles reach the policy library?',
    ],
  },
  {
    prefix: '/observations',
    questions: ['What does programme to be confirmed mean?'],
  },
  {
    prefix: '/people/onboarding/bulk-import',
    questions: [
      'Where is the CSV template?',
      'Which columns does a worker who is not yet in Statura need?', // Answer owner: worker-adoption-bulk-import.
      'How do I import existing worker records from CSV?',
      'Does an imported worker count as having completed onboarding?',
      'What happens when I upload the same worker file again?',
      'Where do I get the worker CSV template and check import dates?', // Answer owner: worker-adoption-bulk-import.
    ],
  },
  {
    prefix: '/hr/onboarding/bulk-import',
    questions: [
      'How do I import many agency workers at once?',
      'Does missing pay setup stop roster activation for an imported worker?',
      'How do I retry rejected rows from an agency CSV import?',
    ],
  },

  // Contractor evidence direct upload (UUID segments are stripped).
  {
    prefix: '/contractors/evidence/new',
    questions: [
      'How do I upload contractor evidence for a contractor?',
      'How do I mark contractor evidence as not applicable?',
    ],
  },
  {
    prefix: '/contractors/new',
    questions: ['Can I attach an insurance certificate when adding a contractor?'],
  },
  {
    prefix: '/contractors',
    questions: [
      'I am on /contractors. How do screening uploads reach a linked worker?',
      'I am on /contractors. How do I preview a contractor document?',
      'How do I mark contractor evidence as not applicable?',
      'Why is a service recorded from contractor evidence held?',
    ],
  },
  {
    prefix: '/me/contractor',
    questions: [
      'Why can’t I see my contractor record?',
      'Why can I see my contractor portal as a care worker?',
    ],
  },
  // Contractor management W1 record tabs (UUID segments are stripped).
  {
    prefix: '/contractors/people',
    questions: [
      'How do I link a sole trader to a worker record?',
      'How do I add the people a contracting business sends?',
    ],
  },
  {
    prefix: '/contractors/bank-account',
    questions: [
      'Who approves contractor bank details?',
      'Why must I confirm contractor bank details before approving?',
    ],
  },
  {
    prefix: '/head-office/board-pack',
    questions: ['How does the head-office board pack relate to other board-pack workspaces?'],
  },
  { prefix: '/analytics', questions: ['Why could screening not be checked in Analytics?'] },
  {
    prefix: '/people/workers/administration',
    questions: ['Why is login readiness unavailable in Worker administration?'],
  },
  {
    prefix: '/analytics/board-pack',
    questions: ['Where do I manage scheduled board packs?'],
  },
  {
    prefix: '/analytics',
    questions: ['Why does Finance see screening counts without worker names in Analytics?'],
  },
  {
    prefix: '/onboarding',
    questions: [
      'Where do I configure SIRS notification routing during onboarding?',
      'How do I record our operating model and registration categories during onboarding?',
      'Where can Essentials import worker compliance records?',
      'How do I retry a failed worker invite without creating another worker?',
    ],
  },
  {
    prefix: '/admin/system/sso',
    questions: [
      'How do I confirm the HR job role and employment type for new SSO workers?',
      'Why can’t SCIM or SSO create a worker until the mapping is saved?',
    ],
  },
  {
    prefix: '/clinical/medication-incidents',
    questions: [
      'How do I record a medication error discovered outside eMAR?',
      'How do I capture and correct medication event evidence without delaying an incident report?',
    ],
  },
  {
    prefix: '/compliance/clinical-governance',
    questions: ['What does the clinical governance view count?'],
  },
  {
    prefix: '/compliance/registration',
    questions: [
      'How do I review current provider categories and legacy registration history?',
      'Where do I review provider obligations and New Aged Care Act registration conditions?',
      'Why is legacy registration history unavailable?',
    ],
  },
  {
    prefix: '/people/compliance/screening',
    byProgram: {
      RAC: [
        'How does a Compliance Officer go from the screening register to a worker and get another person to verify?',
      ],
    },
    questions: [
      'Where do I find draft pending-application declaration wording?',
      // Answer owner: workforce-screening-register; exact revision and independent decision.
      'I am on /people/compliance/screening. How do I independently accept or reject a submitted credential revision?',
      'How does a Compliance Officer go from the screening register to a worker and get another person to verify?',
      'Why could screening obligations not be checked?',
      'Where do I review imported First Aid and CPR evidence?',
      'How does screening verification update worker readiness?',
      'How do I review CHSP or NATSIFACP screening without interrupting care?',
      'How do I record and independently verify screening evidence?',
      'How do I attach the evidence file when I add a screening record?',
      'Why can’t I verify a screening record I edited?',
      'Who can verify screening evidence as the independent second person?',
      'How do I review a precluding screening outcome?',
      'How do I record a screening managed in another system?',
      'What should I do when a rostered worker has expired screening?',
      'Why are banning orders and work rights checked in every roster enforcement mode?',
      'How do I verify several imported screening records after reviewing their evidence?',
      'On People > Compliance, why did a worker’s screening status change without a new record?',
      'Which workers can an HR Officer review screening for?',
      'Which workers can a registered nurse see screening for?',
      'What do the 30-day and 60-day expiry counts include?',
      'Where do older screening bookmarks open?',
    ],
  },
  {
    prefix: '/people/compliance',
    questions: [
      'Why are worker compliance totals incomplete?',
      'How do I record or correct provider-held training for lite and full workers?',
      'How do I record provider training that is not in the catalogue?',
      'How do I add a worker who is not on the roster?',
      'How do I add a care recipient for compliance only?',
      'What should I do if a compliance register or assignment check is unavailable?',
      'What can Quality Officers and Compliance Officers read in the obligations register?',
      'How do I promote a compliance-only worker without re-entering known details?',
      'How do I book reviewed renewals in residential mode?',
      'How do I track policy acknowledgements for compliance-only workers?',
      'What do Recorded, Reviewed, Verified and Unknown mean in compliance-only Getting Started?',
      'How do I review compliance history for a compliance-only worker or recipient?',
      'How do I find active or inactive compliance-only people?',
      'How do I edit or deactivate a compliance-only worker?',
      'How do I edit or archive a compliance-only care recipient?',
      'How do I reactivate a compliance-only worker?',
      'How do I restore an archived compliance-only recipient?',
      'I am on /people/compliance?view=renewals. How do I filter renewals, read the Leaving on tag and what happens to worker and manager reminders during notice?',
      'I am on /people/compliance. How do I read compliance statuses and find import or export?',
    ],
  },
  {
    prefix: '/compliance/policies',
    questions: [
      'What should I do if a policy lifecycle update cannot be confirmed?',
      'How do I refresh a policy that still cites the 1997 Act?',
    ],
  },
  {
    prefix: '/people/policies-conduct/publications',
    questions: ['Where do I find policy publication evidence?'],
  },
  {
    prefix: '/people/policies-conduct/governance',
    questions: [
      'How do I send a governance policy for worker acknowledgement?',
      'How do I create and upload a provider-owned governance policy?',
    ],
  },
  {
    prefix: '/people/policies-conduct/policies',
    questions: [
      'How can a Compliance Officer author and publish worker policies?',
      'Why does a governance policy not create a worker acknowledgement assignment?',
    ],
  },
  {
    prefix: '/people/policies-conduct/cases',
    questions: [
      'I am on /people/policies-conduct/cases. Which views can I open?',
      'I am on /people/policies-conduct/cases. How do I record the Queensland suitable duties notice?',
      'I am on /people/policies-conduct/cases. What happens after I justify an action or raise a review concern?',
      'I am on /people/policies-conduct/cases. How do I check my scheme reminder and copy a suitable duties notice?',
      'I am on /people/policies-conduct/cases. What should I do if a grievance suggests harm to a resident or client?',
      'Who can open the Code bundle and conduct case pointers?',
      'How do I record a grievance a worker raised with me?',
      'What happens when I mark a grievance as a possible protected disclosure?',
      'How do I correct an outcome I gave a worker?',
      'I am on /people/policies-conduct/cases. How do I record that the insurer was notified of a Tasmania claim?',
      'I am on /people/policies-conduct/cases. How do I add or correct the date the worker reported an injury?',
      'I am on /people/policies-conduct/cases. How do the scheme reminders work?',
      'How do I open a return-to-work case for an injured worker?',
      'Why can’t I record contact with the worker’s doctor?',
      'How do I put a worker’s restrictions on the roster?',
      'Why does this action need a written justification?',
      'How do I review a justification for action against a worker?',
      'How do I check a login that was deactivated?',
    ],
  },
  {
    prefix: '/people/policies-conduct/code',
    questions: [
      'Who can open the Code bundle and conduct case pointers?',
      'Does a legacy Code acknowledgement prove current completion?',
      'What happens after a conduct case is substantiated?',
    ],
  },
  {
    prefix: '/people/conduct-decisions',
    questions: ['How do I decide whether a substantiated conduct case needs a work restriction?'],
  },
  {
    prefix: '/people/policies-conduct/code/reports',
    questions: ['How do I download an audited Code of Conduct report?'],
  },
  {
    prefix: '/compliance/prudential/reports',
    questions: [
      'How do I prepare an annual accommodation statement?',
      'Why does a financial report require confirmation?',
      'What belongs in the annual prudential compliance statement?',
    ],
  },
  {
    prefix: '/compliance/star-ratings',
    questions: ['Will this show our live star rating?'],
  },
  {
    prefix: '/compliance/prudential/deposits',
    questions: [
      'Why is my refund figure estimated, held or below pending retention?',
      'Why is a deposit held while its balance is being reconciled?',
      'Who can preview and process a prudential refund?',
      // Also covers unavailable name/class enrichment while posting history remains visible.
      'Why does retention history say Unavailable?',
      'What happens to the previous agreement when I record a new accommodation agreement?',
      'Why is MPIR for my agreement date not loaded yet?',
      'Who can record prudential deposits, refunds and permitted uses?',
      'How is a residential daily accommodation payment fixed on the price agreement day?',
      'How do I post deposit retention for a month?',
      'What should I do with estate evidence and missed retention?',
      'How do I record a refund delay agreement or estate evidence?',
      'Why are correction refund obligations unavailable on the deposits page?',
      'Why does a deposit show Deferred by agreement?',
    ],
  },
  {
    prefix: '/compliance/prudential/permitted-uses',
    questions: ['Who can record prudential deposits, refunds and permitted uses?'],
  },
  {
    prefix: '/compliance/prudential',
    questions: [
      'How do I record a prudential applicability decision?',
      'How do I prepare a prudential evidence pack?',
    ],
  },
  {
    prefix: '/compliance/restrictive-practices/new',
    questions: ['How do I save a restrictive-practice use before its type is known?'],
  },
  {
    prefix: '/compliance/restrictive-practices',
    questions: [
      'How do I add NDIS evidence after recording restrictive-practice use?',
      'What should I do if a behaviour support plan lifecycle update cannot be confirmed?',
      'How do I save a restrictive-practice use before its type is known?',
      'What if I cannot yet classify a possible restrictive-practice event?',
      'How do I triage a restrictive-practice use for SIRS from its episode?',
      'How do I record the actual time of an emergency restrictive-practice use?',
      'How do I record Commission submission evidence for a restrictive practice?',
      'How do I manage a Behaviour Support Plan (BSP)?',
      'What consent and authorisation should I record for a restrictive practice?',
      'How do I record medicine details when the chart ID is unavailable?',
    ],
  },
  {
    prefix: '/compliance/whs/incidents',
    questions: [
      'Can I record a WHS incident before all description details are known?',
      'How do I add staff or care recipients to a WHS incident?',
      'How are people and unknown answers shown on the WHS incidents list?',
      'How long is an unsaved WHS incident draft kept on this device?',
      'What if care recipient search is unavailable?',
      'What if involved people are unavailable on a WHS incident?',
      'How do I record contact with an electrical, water or other authority?',
      'How do I decide whether a WHS incident is notifiable?',
      'How do I record a WHS regulator notification without a reference number?',
      'How do I link a WHS incident and incident for the same event?',
      'How do I close or reopen a WHS incident?',
      'How do I get back to the WHS incident register or report another WHS incident?',
      'Where can I see how a WHS incident was closed or reopened?',
      'Why can I not record or correct a WHS regulator notification?',
      'How are existing WHS incidents numbered?',
      'How do I record a body part, nature, mechanism and agency for an injury?',
      'Do I have to classify every injury?',
      'How do I record that a resident was also harmed?',
      'How do I finish reports from the one-door form?',
      'How do I reopen a dismissed pending report?',
      'Why is there no deadline on Tell the agency about this incident?',
      'How do I record substance exposure or a Vehicle incident?',
      'Who receives an urgent possibly notifiable WHS alert?',
      ...ONE_DOOR_QUESTIONS,
      'How do I switch from the WHS incident form to the combined form?',
      'How do I record an agency worker in a WHS report?',
    ],
  },
  {
    prefix: '/compliance/whs/chemicals',
    questions: ['What should I do if a chemical register status update cannot be confirmed?'],
  },
  {
    prefix: '/compliance/whs/psychosocial',
    questions: [
      'How do I assess psychosocial hazards and follow up controls?',
      'What should I do if a psychosocial register status update cannot be confirmed?',
    ],
  },
  {
    prefix: '/compliance/whs/drills',
    questions: ['How do I schedule a drill, record its outcome and follow up corrective actions?'],
  },
  {
    prefix: '/compliance/whs',
    questions: [
      'How do I manage workplace health and safety?',
      'How do I view a drill and its saved outcome?',
      'Why are emergency drill records unavailable?',
    ],
  },
  {
    prefix: '/compliance/ipc/surveillance',
    questions: [
      'What should I do if an infection surveillance status update cannot be confirmed?',
      'How do I record an infection for a compliance-only recipient?',
      'How do I open the exact IPC record from a person’s compliance history?',
      'How do I link an infection to an outbreak?',
    ],
  },
  {
    prefix: '/compliance/ipc/outbreaks',
    questions: [
      'What should I do if an outbreak status update cannot be confirmed?',
      'How can I tell when an outbreak notification target is overdue?',
      'How do I open the exact IPC record from a person’s compliance history?',
      'How do I record who was exposed during an outbreak?',
      'How do I record a suspected community outbreak and public health notification?',
      'Can I record a suspected outbreak before I know its location?',
      'How do I confirm an outbreak location later?',
    ],
  },
  {
    prefix: '/compliance/ipc',
    questions: [
      'How do I choose who receives outbreak tasks?',
      'How do I hand over an IPC lead appointment?',
      'How do I view every IPC exposure and restriction?',
      'How do I manage infection prevention and control?',
      'How do I record IPC lead coverage?',
      'How do I complete an outbreak notification response?',
    ],
  },
  {
    prefix: '/compliance/data-breach',
    questions: [
      'How do I open a breach from another event?',
      'How do I track breach remediation?',
      'How do I record a privacy or data breach?',
      'How long do I have to assess a possible Notifiable Data Breach?',
      'How do I prepare an NDB statement?',
      'How do I retry a prepared NDB statement download?',
      'How do I correct the awareness time for a data breach?',
      'How do I record an affected person and individual notice?',
      'How do I attest an external NDB lodgement?',
      'How do I complete a data breach notification response?',
    ],
  },
  {
    prefix: '/compliance/governance/qcab',
    questions: [
      'How do I review QI figures in QCAB?',
      'How do I review source records before publishing a QCAB report?',
      'Where do I open the QCAB recording forms?',
      'How do I correct a historical QCAB evidence assembly?',
    ],
  },
  {
    prefix: '/compliance/governance/risk-register/new',
    questions: [
      'How is the risk review date suggested?',
      'How do I link a risk to its source record?',
    ],
  },
  {
    prefix: '/compliance/governance/risk-register/suggested',
    questions: ['How do I add suggested risks?', 'How is the risk rating calculated?'],
  },
  {
    prefix: '/compliance/governance/skills-matrix',
    questions: [
      'What does the governance applicability banner mean?',
      'Why is skills coverage unavailable while board members are visible?',
    ],
  },
  {
    prefix: '/compliance/governance/conflicts',
    questions: [
      'What does the governance applicability banner mean?',
      'How do I view a declaration before marking it reviewed?',
    ],
  },
  {
    prefix: '/compliance/audit-pack/export',
    questions: [
      'How do I generate a workforce audit ZIP and record actual external submission evidence?',
      'How do I correct a submission time or retry recording the same receipt?',
    ],
  },
  {
    prefix: '/compliance/governance',
    questions: [
      'How do I generate and download a compliance board pack?',
      'How do I schedule a de-identified board pack?',
      'How do I disable a board pack schedule?',
      'Where are the board-pack workspaces?',
      'What does the governance applicability banner mean?',
    ],
  },
  {
    prefix: '/compliance/evidence-packs',
    questions: [
      'How do I prepare an evidence assembly?',
      'Where do I open Prepare pack?',
      'How are Standards suggested for an evidence pack?',
      'How do I share an evidence pack with our registered provider?',
    ],
  },
  {
    prefix: '/compliance',
    questions: [
      'How do I resolve work from the Compliance home?',
      'I am on /compliance. What do unavailable sources and limited history mean?',
      'Where are Events, Quality, Governance and People on compliance-only plans?',
      'How do I unpin Calendar from the compliance sidebar?',
    ],
  },
  {
    prefix: '/settings/compliance',
    questions: [
      'How do I require an explicit SIRS decision for every incident?', // Answer owner: settings-compliance-command-access.
      'How do I choose optional contractor compliance controls?',
      'How do I review effective compliance settings?',
      'How do I preview and save a compliance target?',
      'How does a facility manager set an assigned facility compliance target override?',
      'How do I edit the current complaint outcome letter wording?',
      'How do I rename a complaint category?',
      'What happens when I retire a complaint category?',
      'Why does a compliance Settings control say Ask your organisation administrator?',
    ],
  },
  {
    prefix: '/compliance/record-recovery',
    questions: [
      'How do I recover regulated captures left on a shared device?',
      'Why does the recovery queue say unavailable?',
      'Does reconciling a retained medication capture clear its safety warning?',
    ],
  },
  {
    prefix: '/compliance/audit-program',
    questions: [
      'Where does Add to risk register appear and what does it do?',
      'How do I assign and verify an audit corrective action?',
      'How do I retry an improvement action after a major finding?',
      'Can I assess a Standard that the provider profile suggests does not apply?',
      'Where does an improvement from an audit assessment appear?',
      'How do I view legacy audit plan evidence?',
    ],
  },
  {
    prefix: '/dashboards/compliance',
    questions: [
      'What does SCREENING GAPS count on the compliance dashboard?',
      'Why is the screening section unavailable on the compliance dashboard?',
      'How does the compliance dashboard calculate its SIRS countdowns and Standards score?',
      'Why does the Essentials compliance dashboard show only some worker checks and draft improvements?',
      'Why does audit or governance ask me to review provider facts?',
    ],
  },
  {
    prefix: '/compliance/work',
    questions: [
      'How do I resolve work from the Compliance home?',
      'How do I search compliance work by owner name?',
      'I am on /compliance. What do unavailable sources and limited history mean?',
      'I am on /compliance/work. Why are deadline-state counts unavailable and SIRS rows excluded from bulk selection?',
      'What should I work on first?',
      'Why are repeated duty dates grouped?',
      'How do I save provider duty and requested complaints report reminders?',
      'How do I clear an annual complaints report review target?',
      'Can I set a different landing page after login?',
      'What is My Compliance Work?',
      'Where do open disclosure provider targets appear in My compliance work and calendar?',
      'Why do CHSP setup prompts appear in My Compliance Work?',
      'How do I correct an obsolete historical plan that is still editable?',
      'Do I have to claim an item before I can act on it?',
      "What is a 'recommended practice deadline'?",
      'Where do I configure compliance escalation?',
      'Who receives a compliance escalation?',
      'What happens if an escalation role has no active holder?',
      'Why are statutory escalation reminders not deferred overnight?',
      'Does escalation change a compliance deadline?',
      'How do approved leave backups receive escalation reminders?',
      'How do I hold reminders for an item?',
      'How do I route escalation to a named person?',
    ],
  },
  {
    prefix: '/settings/registration-providers',
    questions: [
      'How do I confirm approved homes, deposit history and reporting periods?',
      'How do I complete registration answers for an external-only provider?',
      'How do I assign, complete or read a retained programme evidence review task?',
      'How do I configure TAC and DVA external incident contract terms?',
      'How do I complete our compliance profile?',
      'How do I record registration expiry and renewal dates?',
      'Why does Statura show every obligation until we confirm our answers?',
      'How do I record an associated-provider arrangement?',
      'When is an associated-provider Commission notice due?',
      'Can Statura decide whether labour hire is a carve-out?',
      'How do forwarded associated-provider incidents and complaints work?',
      'I am on /settings/registration-providers. Why is my Settings save refused after access changes?',
      'How do I keep our registration, insurance and head-provider agreement files current?',
    ],
  },
  {
    prefix: '/settings/facilities',
    conditionalQuestionsLast: true,
    conditionalQuestions: [
      {
        questions: ['I am on /settings/facilities. How do I verify a site HPI-O?'],
        requiresRoles: [
          'OWNER',
          'TENANT_ADMIN',
          'SYSTEM_ADMIN',
          'COORDINATOR',
          'CASE_MANAGER',
          'CLINICAL_LEAD',
        ],
        requiresIntegration: 'hi',
        requiredModules: ['/settings/integrations/hi'],
      },
    ],
    questions: [
      'How do I record a facility’s historical AN-ACC funding category?',
      'How do I change a facility timezone?',
      'How do I add a service region without a street address?',
    ],
  },
  {
    // Organisation > Organisation details; the registration card still saves separately.
    prefix: '/settings/organisation',
    conditionalQuestionsLast: true,
    conditionalQuestions: [
      {
        questions: ['I am on /settings/organisation. How do I verify our provider HPI-O?'],
        requiresRoles: [
          'OWNER',
          'TENANT_ADMIN',
          'SYSTEM_ADMIN',
          'COORDINATOR',
          'CASE_MANAGER',
          'CLINICAL_LEAD',
        ],
        requiresIntegration: 'hi',
        requiredModules: ['/settings/integrations/hi'],
      },
    ],
    questions: [
      'Where do I save our confirmed ACQSC registration ID?',
      'What does One-person organisation relax?',
      'How do I allow self-review for client notes?',
    ],
  },
  {
    prefix: '/compliance/workforce/import',
    questions: [
      'How do I purge expired workforce import payloads?',
      'How do I import workers, screening records, training evidence, or policy acknowledgement evidence from a CSV?',
      'Do I need to create a course or policy before importing external evidence?',
    ],
  },
  {
    prefix: '/people/compliance/certifications',
    questions: [
      'How can I view my own certifications?',
      'Who can record a verified worker certification?',
      'How do I attach a certificate file when I record a certification?',
      'How do I require a second person to verify certification evidence?',
      'What do the 30-day and 60-day expiry counts include?',
    ],
  },
  {
    // Record grant shortcuts, tenant-local As of and failed-load retry are in the answer.
    prefix: '/finance/natsifacp',
    questions: [
      'How do I record NATSIFAC home-care grant places?',
      'How do I record grant acquittal and contribution terms?',
    ],
  },
  {
    prefix: '/finance/claims',
    questions: [
      'How do I prepare a DVA Community Nursing claim?',
      'How do I record separate payments with the same reference?',
    ],
  },
  {
    prefix: '/compliance/reporting/dex',
    questions: [
      'How do I resolve MAC ID work or correct CHSP reporting coverage?',
      'How do I retry unavailable DEX reporting data or DSS evidence?',
      'Where do I find CHSP My Aged Care ID completeness work?',
      'When is our CHSP monthly DEX report due?',
      'How do I record a rejected DEX upload and its correction?',
      'Where do I confirm our CHSP registration categories and reporting scope?',
      'How do I resolve a CHSP reporting setup diagnostic?',
      'How do I reload and retry a DEX change that was updated by someone else?',
    ],
    chspQuestions: [
      'How do I prepare a CHSP DEX reporting batch?',
      'How do I include late CHSP captures before DEX upload?',
      'How do I release a held CHSP capture for DEX?',
    ],
  },
  {
    prefix: '/settings/leave-policies',
    questions: [
      'How do I set up our first leave policy?',
      'Why can I not publish my own leave policy draft?',
      'How can the only administrator publish a leave policy?',
      'Why is a leave type unavailable for these dates?',
    ],
  },
  {
    prefix: '/settings/security',
    conditionalQuestions: [
      {
        questions: ['What MFA and passphrase policy applies while B2G is on?'],
        requiresIntegration: 'dhda_b2g',
        requiresRoles: [...DHDA_INTEGRATION_ROLES, 'OWNER'],
        requiredModules: ['/settings/integrations/dhda'],
      },
      {
        questions: ['How do I set B2G MFA verification frequency?'],
        requiresIntegration: 'dhda_b2g',
        requiresRoles: ['TENANT_ADMIN'],
        requiredModules: ['/settings/security'],
      },
    ],
    questions: ['How do I change the session inactivity timeout?'],
  },
  {
    prefix: '/settings/users',
    questions: [
      'Why was the user created but the first-login email held?',
      'How do I deactivate or reactivate a user account?',
      'What if more than one account uses the same email with different capitalisation?',
      'Can I assign a Compliance Officer or Quality Director role?',
      'Can I assign the HR officer role?',
      'Can I edit or reactivate a user who has a regional role?',
      'What can an HR officer see and change in People?',
      'Can Payroll or Finance open a worker record?',
      'I am on /settings/users. Why is my Settings save refused after access changes?',
      'Can I manage users before our organisation has a programme?',
      "How do I reset a user's password?",
    ],
  },
  {
    prefix: '/settings/notification-routing',
    questions: [
      'Who receives worker assignment alerts?',
      'How do I route data breaches, outbreaks and screening expiry?',
      'What does the complaint ageing reminder use?',
      'Why does a routing or notification-default control say Ask your organisation administrator?',
      'Does a failed organisation lookup mean an alert was sent?',
      'Which timezone do after-hours escalation steps use?',
      'How do I choose who is alerted about a death or serious injury?',
      'How do I configure provider notification recipients and channels?',
      'How can I check whether notification response work has cover and an owner?',
      'What do notification registration health checks mean?',
      'How can I see who changed a notification policy and when?',
      'How do I delegate notification policy access and review changes?',
      'I am on /settings/notification-routing. Who receives policy acknowledgement reminders?',
      'I am on /settings/notification-routing. How do I preview a catalogue version change?',
      'How do I complete notification routing quick setup?',
    ],
  },
  {
    // Personal notification choices. Everyone who works for the provider can
    // open this, including care workers, so the questions must not assume an
    // administrator is reading them.
    prefix: '/settings/notification-preferences',
    questions: [
      'How do I stop getting a notification?',
      'Why can I not turn off in-app notifications for incidents?',
      'Why am I still getting emails after turning a category off?',
      'How do I get a daily digest for one compliance register?',
      'I am on /settings/notification-preferences. Why is my Settings save refused after access changes?',
    ],
  },
  {
    prefix: '/settings/facilities',
    conditionalQuestionsLast: true,
    conditionalQuestions: [
      {
        questions: ['I am on /settings/facilities. How do I verify a site HPI-O?'],
        requiresRoles: [
          'OWNER',
          'TENANT_ADMIN',
          'SYSTEM_ADMIN',
          'COORDINATOR',
          'CASE_MANAGER',
          'CLINICAL_LEAD',
        ],
        requiresIntegration: 'hi',
        requiredModules: ['/settings/integrations/hi'],
      },
    ],
    questions: ['How do I set RN 24/7 applicability for a residential home?'],
  },
  {
    prefix: '/compliance/rn-coverage/reporting',
    requiredModules: ['/settings/integrations/dhda'],
    requiresIntegration: 'dhda_b2g',
    requiresRoles: [...DHDA_INTEGRATION_ROLES],
    questions: [
      'How do I review and attest the exact B2G RN payload?',
      'What privacy warning must I review before an RN report?',
      'Why do I need MFA before submitting an RN report?',
      'What should I do if the RN submission outcome is uncertain?',
      'How are RN reporting status badges labelled?',
    ],
  },
  {
    prefix: '/settings/security',
    questions: [
      'How do I enrol an authenticator app?',
      'At Settings > Security, how do I require MFA for administrators in my tenant?',
      'What password length is required when I change my password?',
      'Why does a Settings command ask me to contact our organisation administrator?',
    ],
  },
  {
    prefix: '/settings/integrations/dhda',
    requiredModules: ['/settings/integrations/dhda'],
    requiresIntegration: 'dhda_b2g',
    requiresRoles: [...DHDA_INTEGRATION_ROLES],
    conditionalQuestionsLast: true,
    conditionalQuestions: [
      {
        questions: ['How do I run the safe DHDA B2G connection check?'],
        requiresIntegration: 'dhda_b2g',
        requiresRoles: [...DHDA_INTEGRATION_SETUP_ROLES],
        requiredModules: ['/settings/integrations/dhda'],
      },
    ],
    questions: [
      'Who can configure the DHDA connection?',
      'Do accepted B2G API tests mean production access is ready?',
      'Which roles can view B2G reporting data?',
      'Why was my B2G submission refused?',
    ],
  },
  {
    prefix: '/auth/mfa/enroll',
    questions: [
      'Why do I need to enrol an authenticator before continuing?',
      'How do I finish MFA enrolment?',
      'How do I set up two-step sign-in?',
    ],
  },
  {
    prefix: '/reset-password',
    questions: ['What password length is required when I reset my password?'],
  },
  {
    prefix: '/compliance/rn-coverage',
    questions: [
      'Why does my RN monthly report reminder remain visible?',
      'Why is my RN monthly report register Limited?',
      'Why is today’s RN coverage provisional?',
      'How do I review completed RN coverage?',
      'How do I complete and lodge the monthly RN report?',
      'How do I record the actual GPMS lodgement time?',
    ],
  },
  {
    prefix: '/compliance/quality/qi-data-entry/reporting',
    requiredModules: ['/settings/integrations/dhda'],
    requiresIntegration: 'dhda_b2g',
    requiresRoles: [...DHDA_INTEGRATION_ROLES],
    questions: [
      'How do I review and attest the exact B2G QI payload?',
      'What privacy warning must I review before a QI report?',
      'Why do I need MFA before submitting a QI report?',
      'What should I do if the QI submission outcome is uncertain?',
      'How are QI answer badges labelled?',
    ],
  },
  {
    prefix: '/compliance/b2g/audit',
    requiredModules: ['/settings/integrations/dhda'],
    requiresIntegration: 'dhda_b2g',
    requiresRoles: [...DHDA_INTEGRATION_ROLES],
    questions: [
      'How do I export the B2G audit log?',
      'Which roles can view and export B2G audit events?',
      'What information is included in the B2G audit CSV?',
      'How do I download every file in a large B2G audit export?',
    ],
  },
  {
    prefix: '/settings/integrations',
    conditionalQuestions: (
      [
        ['services_australia', 'Services Australia', '/settings/integrations/services-australia'],
        ['hi', 'Healthcare Identifier', '/settings/integrations/hi'],
        ['dhda_b2g', 'DHDA B2G', '/settings/integrations/dhda'],
      ] as const
    ).map(([requiresIntegration, label, path]) => ({
      requiresIntegration,
      requiresRoles: ['OWNER', 'TENANT_ADMIN'],
      requiredModules: [path],
      questions: [
        `How do I monitor ${label} connection health?`,
        `How do ${label} expiry notices work?`,
        `How do I safely retry a ${label} operation?`,
        `How do I export ${label} operations?`,
        'I am on /settings/integrations. Why is my Settings save refused after access changes?',
      ],
    })),
    questions: [
      'How do I manage integrations for my organisation?',
      'At Settings > Integrations, which security setting is required before enabling a connection?',
      'Why are connection settings hidden when a government connection is off?',
    ],
    requiresRoles: ['OWNER', 'TENANT_ADMIN'],
  },
  ...[
    [
      '/settings/integrations/services-australia/conformance',
      'How do I use Services Australia Conformance tests?',
    ],
    [
      '/settings/integrations/services-australia/event-reconciliation',
      'How do I review Event Search reconciliation?',
    ],
  ].map(([prefix, question]) => ({
    prefix: prefix!,
    questions: [
      question!,
      ...(prefix === '/settings/integrations/services-australia/event-reconciliation'
        ? ['How do I use an entry already held by the agency?']
        : []),
    ],
    requiresIntegration: 'services_australia' as const,
    requiresRoles: [...SERVICES_AUSTRALIA_INTEGRATION_ROLES],
    requiredModules: ['/settings/integrations/services-australia'],
  })),
  {
    prefix: '/settings/integrations/services-australia/mapping',
    questions: ['How do I review a Services Australia service mapping?'],
    requiresIntegration: 'services_australia',
    requiresRoles: [...GOVERNMENT_PROVIDER_SETUP_CONFIG_ROLES],
    requiredModules: ['/settings/integrations/services-australia'],
  },
  {
    prefix: '/settings/integrations/services-australia',
    questions: [
      'How do I set up assisted Services Australia reads?',
      'How do I fix SAH Service ID not mapped?',
      'How do I choose the Care Recipient Summary version?',
      'How do I set up a new Vendor/iTest provider scope?',
      'How do I correct Services Australia NOI evidence?',
      'How do I capture an authorised NOI missing-recipient response?',
      'How do I run the safe Services Australia connection check?',
      'Can I approve a Services Australia request I prepared?',
    ],
    requiresIntegration: 'services_australia',
    requiresRoles: [...GOVERNMENT_PROVIDER_SETUP_CONFIG_ROLES],
  },
  {
    prefix: '/settings/integrations/hi',

    requiresIntegration: 'hi',
    requiresRoles: ['OWNER', 'SYSTEM_ADMIN', 'TENANT_ADMIN'],
    questions: [
      'How do I prepare Healthcare Identifier (HI) integration?',
      'How do I record HI credential installation and run the independent checker?',
      'How do I run the safe HI connection check?',
      'What does HI vendor test readiness mean?',
      'Who installs NASH credentials for HI?',
      'Does turning off HI stop care delivery?',
      'How do I restore a paused or revoked HI connection?',
      'Why can’t I change an HI connection?',

      'I am on /settings/integrations/hi. How do I record the SVT NASH installation and obtain independent approval?',
    ],
  },
  {
    prefix: '/settings/integrations/dhda',
    requiresIntegration: 'dhda_b2g',
    requiresRoles: ['SYSTEM_ADMIN', 'TENANT_ADMIN'],
    questions: [
      'How do I prepare DHDA B2G reporting integration?',
      'How do I run the safe DHDA B2G connection check?',
      'How do I record a provider instance registration with DHDA?',
      'How do I send DHDA Authentication Registration safely?',
      'How do I rotate or revoke a B2G machine credential?',
      'Where do I record GPMS provider identifiers and the Software Identifier?',
      'Why can organisation administrators not run B2G API smoke tests here?',
      'How do operational accounts submit QI or RN reports?',
      'How do I export the B2G audit log?',
      'What should I do when a DHDA request fails?',
      'How do I restore a paused or revoked B2G connection?',
      'Why can’t I change a B2G connection?',
    ],
  },
  {
    prefix: '/overview',
    questions: [
      'How do I start my day on Home?',
      'How do I customise Home?',
      'How do I find a client from Home?',
      'Where is All Care Plans?',
      'Where are assessments?',
      'What is in the facility lead sidebar?',
      'Can I set a different landing page after login?',
      'Where is Executive View?',
      'Where is Dispatch?',
      "Where is today's roster?",
      'How do I publish a Home layout for a role?',
      'What if Home has no cards?',
      'Can we set up our team before adding our first client?',
      'How do I get around on a compliance-only plan?',
      'I am on /overview. Why does my workspace say Checking workspace access or Workspace access unavailable?',
      'What does the SIRS countdown on Home include?',
    ],
  },
  {
    prefix: '/dashboards/home-care-ops',
    questions: [
      'How do I add Home Care Operations tiles to Home?',
      'Does Client budget risk include all active Support at Home plans?',
      'How do I start my day on Home?',
      'How do I record a Support at Home visit?',
    ],
  },
  {
    prefix: '/care-plans',
    questions: [
      'How do I edit residential care-minute targets in a care plan?',
      'Where is All Care Plans?',
      'How do I start a care plan?',
      'What should I do if a care plan change cannot be saved?',
      'Why is Create signing link unavailable for participant care plan acknowledgement?',
      'How do I add a service to a care plan?',
      'How does a Support at Home amendment use the current quarter budget?',
      'How do I record planned restrictive practice on a Support at Home care plan?',
      'How do I review assessment findings on an active care plan?',
      'How do I activate an amendment after Request approval?',
      'Why is my agreement awaiting provider signature?',
      'How do I find the right client in a shared picker?',
      'How do I record emergency evacuation assistance in a care plan?', // Answer owner: creating-care-plans; Paul Q1/Q2 confirmed conflict override, relevant-domain-only invalidation, equipment and hash/length audit.
    ],
    chspQuestions: [
      'Where is the CHSP transition follow-up worklist?',
      'How do I record a copy of a CHSP agreement or care plan?',
    ],
  },
  {
    prefix: '/assessments',
    questions: [
      'Where are assessments?',
      'How do I hand off saved assessment safety findings?',
      'How do I check the sources of grouped assessment recommendations?',
      'How do I retry unavailable assessment dashboard counts?',
      'Can I start assessments while Support at Home setup is in progress?',
      'How do I retry failed care-plan follow-through?',
      'How do I start my day on Home?',
      'Where do I find Journey?',
      'How do I choose an eligible assessment template?',
      'How do I find the right client in a shared picker?',
    ],
  },
  {
    prefix: '/roster/timesheets',
    questions: [
      'How do I approve timesheets?', // timesheets-and-leave: section navigation, separate queue filters, Help in More actions and View all periods.
      'On /roster/timesheets, how do I recompute approved pay after a pay setup change?', // timesheets-and-leave
      'Can I correct a timesheet for a shift longer than 12 hours?',
      'On /roster/timesheets, what happens if Correct time finds a concurrent change?',
      'Why is a worked-time correction held during a daylight-saving change?',
      'How do I put a rostered worker on leave from Timesheets?',
      // New workflows retain the established six visible questions.
      'On /roster/timesheets, why did Log time link to a rostered shift?',
      'On /roster/timesheets, how do I re-approve a named pay period?', // timesheets-and-leave: reopen the saved review or deliberately start a new review.
      'On /roster/timesheets, how do I fix a timesheet held after approval?',
      'On /roster/timesheets, who checks a public holiday shift without clock evidence?', // timesheets-and-leave: active checks and comparison/off preflight.
      'On /roster/timesheets, how do I record Public holiday – not worked?',
      'On /roster/timesheets, how do I approve a public holiday absence?', // Answer owner: timesheets-and-leave; independent PH absence queue.
      'On /roster/timesheets, how do I resolve an unknown break length?', // timesheets-and-leave
      'Where do finance officers find Timesheets?',
      'Can I log or approve time when the pay role is unresolved?',
      'Why was approval refused for overlapping worked hours?',
      'Why is pay on hold after the hours were approved?',
      'Why is an allowance recorded but not priced?',
      'How do I close an allowance request that approved pay evidence blocks?',
      'How do I retry pending allowance pricing?',
      'On /roster/timesheets, what does an allowance warning after Recompute mean?',
      'How do I remove one timesheet allowance?',
      'How do I approve timesheets one after another without returning to the list?',
      'Why are the RN handover and in-charge answers already filled in?', // timesheets-and-leave: roster-suggested RN allowance answers (D2/O6).
      'On /roster/timesheets, why is there no RN allowance step for a kitchen shift?',
      'How do I add and read timesheet review notes?',
      'How do I review worked time against the roster?',
      'What does Timesheets awaiting verification mean?',
      'How do I export the complete timesheet scope or retry an export?',
      'How do I mark a rostered shift as not worked?',
      'How do I keep the same period between Schedule and Time & closeout?',
      'Can I review leave requests while balances are unavailable?',
      'How do I send leave to payroll?',
      'How do I correct an approved timesheet?',
      'What does Held mean in Leave payments?',
      'Why does Leave payments say annual leave loading is not ready to send?',
      'Why does Leave payments say this leave is already in Xero?',
      'How do I reopen an approved timesheet?',
      'When do timesheets start after a Master roster is applied?',
      'How do I review a leave request and use Status or More?', // timesheets-and-leave: New request and Submit request.
      'How do I refresh the leave list after approval asks me to review?',
      'How do I review a travel claim?',
      'How do I record a sleepover or on-call availability window on a timesheet?',
      'How do I repair an in-charge allowance still being applied to pay?', // Includes approved snapshot mismatches and explicit nurse classification review.
      'How do I retry failed pay processing?',
      'What should I do when pay processing checks cannot load?',
      'What does the Blocked timesheet queue show?',
      'Why are timesheet queue totals unavailable?', // timesheets-and-leave: shared whole-period counts, bounded reconciliation and Refresh recovery.
      'How do I override a Xero pay period status?',
      'On /roster/timesheets, how do default meal breaks and zero minutes work?',
      'On /roster/timesheets, which meal break rule applies to this shift?',
      'How do I use the quiet roster header and controls?',
      'On /roster/timesheets, how do I correct a break presumed from roster?', // timesheets-and-leave
    ],
    roleQuestions: [
      {
        requiresRoles: [
          'OWNER',
          'TENANT_ADMIN',
          'SYSTEM_ADMIN',
          'PAYROLL_OFFICER',
          'FINANCE_OFFICER',
        ],
        questions: ['On /roster/timesheets, how do I re-approve a named pay period?'],
      },
    ],
  },
  {
    prefix: '/me/pay-documents',
    questions: ['How do I read my itemised pay document?'],
  },
  {
    prefix: '/me/timesheets',
    questions: [
      'How do I submit my timesheet?',
      'How do I submit a travel claim?',
      'Can I change a mobile travel claim while it is queued offline?',
      // Includes per-visit accumulation/removal and conservative service-local home labels.
      'How do I record both kinds of kilometres in the worker app?',
      'What if the mobile travel draft was not saved?',
      'How do I review my hours?',
      'Why are my worker hours empty for this period?',
      'How do I request leave?',
    ],
  },
  {
    prefix: '/payroll-activation',
    requiresRoles: ['SYSTEM_ADMIN'],
    questions: ['On /payroll-activation, how do I activate new payroll for one provider?'],
  },
  {
    prefix: '/settings/payroll/activation',
    requiresRoles: ['SYSTEM_ADMIN'],
    questions: ['How does a system administrator activate the new payroll for every provider?'],
  },
  {
    prefix: '/settings/payroll',
    questions: [
      'How do I configure payroll before the first pay run?',
      'On /settings/payroll, where are Payroll approval settings for one approver?', // Answer owner: payroll-settings-hub; inline saves, field errors and effective-time receipt.
      'How do I approve a payroll policy change on the new payroll?', // Answer owner: payroll-settings-hub, Approve a payroll policy change.
      'Where do I connect Xero for payroll?',
      'How do I turn on automatic timesheets?',
      'Why is second-person review blocking my pay run?',
      'How do I choose leave settlement for Xero?',
      'On /settings/payroll, how do I change who approves pay and the Xero plan?',
      'On /settings/payroll, how do I cancel or replace a scheduled payroll approval change?',
    ],
  },
  {
    prefix: '/settings/payroll/profile',
    questions: [
      'Why is export target read-only on the payroll profile?',
      'Where do I declare Xero as the export destination?',
      'What belongs on the tenant payroll profile?',
      'How do I connect Xero payroll?',
      'Where do tenant administrators configure enterprise agreements?',
    ],
  },
  {
    prefix: '/settings/payroll/aba-sender',
    questions: [
      'Why is ABA download blocked?',
      'What is self-managed net pay?',
      'How do I configure the ABA sender for payroll disbursement?',
      'Who can edit payroll banking sender settings?',
    ],
  },
  {
    prefix: '/settings/payroll/provider-responsibilities',
    questions: [
      'How do I configure file-lane evidence and payroll mappings?',
      'Who owns tax and super in our payroll handoff?',
      'How do I save the payroll provider profile?',
    ],
  },
  {
    prefix: '/settings/assessments',
    questions: [
      'How do I enable automatic assessment safety handoff?',
      'How do I assign or delegate the automatic safety handoff owner?',
      'Who can enable or approve an assessment instrument?',
      'Why does an assessment Settings control say Ask your organisation administrator?',
    ],
  },
  {
    prefix: '/settings/portal',
    questions: [
      'How do I save optional portal section settings?',
      'Does marking an incident update as read in the portal complete the provider response?',
      'Why does a portal Settings control say Ask your organisation administrator?',
      'Can portal users see clinical notes?',
      'What can a contact person see on the portal Profile page?',
    ],
  },
  {
    prefix: '/settings/cm-task-types',
    questions: [
      'How do I save or archive a CM task type?',
      'Why does a CM task type control say Ask your organisation administrator?',
    ],
  },
  {
    prefix: '/settings/service-execution',
    questions: [
      'How do I change a service execution pack state?',
      'Why does a service execution control say Ask your organisation administrator?',
    ],
  },
  {
    prefix: '/settings/holidays',
    questions: [
      'How do I add a regional public holiday?',
      'Why are state public holidays missing from pay?',
      'Why does a public holiday control say Ask your organisation administrator?',
    ],
  },
  {
    prefix: '/settings/system',
    questions: [
      'Where do I change the organisation timezone?',
      'Does timezone affect payroll periods?',
      'Does Settings delegation change payroll, leave or Xero access?',
      'I am on /settings/system. Why is my Settings save refused after access changes?',
      'I am on /settings/system. How do I delegate Settings access?',
    ],
  },
  {
    prefix: '/settings/payroll/pay-roles',
    questions: [
      'How do I map roster role ADM to a pay role?',
      'How do I find and edit a role in a large pay-role catalogue?',
      'What happens to unsaved fields when I switch pay-role editors?',
      'How do I edit a pay role?',
      'Where do I assign a pay role to a worker?',
      'What does Employment type not set mean on a pay role?',
      'Why does every save of this pay role fail?',
      'Does changing a pay role change what workers are paid?',
      'How do I re-point a pay role to the current rate version?',
      'What changes when I set a worker’s default pay role?',
      'Why does a shift say more than one pay role could fit?',
    ],
  },
  {
    prefix: '/people/workers/bulk-invite',
    questions: ['How do I recover an interrupted worker invitation?'],
  },
  {
    prefix: '/people/workers/new',
    roleQuestions: [
      {
        questions: ['How do I link a worker to my payroll system?'], // Answer owner: xero-mapping-wizard.
        requiresRoles: PAYROLL_LINK_ROLES,
      },
    ],
    questions: [
      'What should I do when a worker record is saved for manual login handoff?',
      'What if more than one account uses the same email with different capitalisation?',
      'How do I add a worker and confirm their engagement?',
      'How can an HR Officer add a worker and confirm a same-name person?',
      'How do I recover an interrupted worker invitation?',
      'Why does Add worker need the organisation timezone?',
      'What if the worker classification or delivery mode is not known yet?',
      'Which agency supplier should I select for an agency worker?',
    ],
  },
  {
    prefix: '/people/workers',
    roleQuestions: [
      {
        questions: ['How do I link a worker to my payroll system?'], // Answer owner: xero-mapping-wizard.
        requiresRoles: PAYROLL_LINK_ROLES,
      },
    ],
    conditionalQuestions: [
      {
        questions: [
          'I am on /people/workers. Where are record facts and the extra worker sections?',
          'I am on /people/workers. How do I open the work restriction form?',
          'I am on /people/workers. Where do I read typical-shift readiness on the worker record?',
          'I am on /people/workers. Where is Pay & classification in the record navigation?',
          'How do I review a worker HPI-I and save search sex?',
          'I am on /people/workers/[id]. How do I look up and apply a worker HPI-I?',
          'How do I use the worker register toolbar and row actions?',
        ],
        requiresIntegration: 'hi',
        requiresRoles: [...STAFF_PROFILE_EDIT_ROLES],
        requiredModules: ['/settings/integrations/hi'],
      },
    ],
    questions: [
      'What can a Compliance Officer see on Worker 360?',
      'What should I do after an uncertain work restriction save?',
      'Who can see worker notes, pay points, payment methods and travel rates?',
      'How do I confirm a worker engagement?',
      'How do I edit a worker’s preferred name or employee number?',
      'How do I recover an interrupted worker invitation?',
      'Where do I find workers awaiting engagement confirmation?',
      'How do I review a worker’s role in compliance events?',
      'Can I complete a worker onboarding journey when pay setup is missing?',
      'How do I use or clear a worker’s imported employee number?',
      'How do I record a worker exclusion and return to work?',
      'How do I backdate a worker rate without changing paid payroll?',
      'How do I repair a gap or duplicate in worker pay profile history?',
      'Does fixing a historical pay profile reprice paid work?',
      'How do I add Administration alongside a worker’s care role?',
      'How do I complete pay set-up after the worker has started?',
      'I am on /people/workers. Why are care-minutes overrides hidden for home care?',
      'How do I set up a salaried worker without an award?',
      'Why does a successful pay save still show held work?',
      'Why does a rate backdate ask me to review approved leave?',
      'How do I record a worker’s pay setup?',
      'How do I change a worker’s employment type from a date?', // Answer owner: worker-pay-profile; Change employment type from a date.
      'Why did pay setup record an end date when I left it blank?',
      'Can I record a first pay setup after the worker started?',
      'How do I update an agreed hourly rate?',
      'How are casual agreed rates entered on a worker Pay profile?',
      'Why does this worker’s pay setup need review?',
      'How do I add a second pay role without replacing the first?',
      'Why can’t I clear the default role?',
      'Why can’t I use a historical or future effective date?',
      'Where do I fix a worker blocking award review?',
      'How do I maintain a worker pay and classification profile?',
      'How do I set a worker’s default pay role?',
      'What should I do when protected worker pay fields cannot be loaded?',
      'How are worker role and employment badges labelled?',
      'I am on /people/workers. Where did Workers move?',
      'I am on /people/workers. How do I replace an offer and prepare the candidate for onboarding?',
    ],
  },
  {
    prefix: '/payroll/fixed-pay',
    questions: [
      'How do I record workers-compensation wage replacement before a pay run?',
      'How do I manage recurring worker allowances before snapshot?', // Includes fixed amount approval and provider evidence.
    ],
  },
  {
    prefix: '/payroll/manual-closures',
    questions: [
      'How do I record manual payroll block closures for a period?',
      'Can a manual closure be reused for another payroll period?',
    ],
  },
  {
    prefix: '/payroll/agency-invoices',
    questions: [
      'How do I generate agency AP invoices for a payroll period?',
      'Are agency invoices the same as employee pay runs?',
    ],
  },
  { prefix: '/head-office/agency-hours', questions: [] },
  {
    prefix: '/roster/leave/coverage',
    questions: [
      'How do I release rostered shifts after approving leave?',
      'What is the leave coverage queue?',
      'How do I use the quiet roster header and controls?',
    ],
  },
  {
    prefix: '/roster/timesheets/reconciliation',
    questions: [
      'What is the period close ledger on timesheets?',
      'How does roster assignment reconciliation differ from payroll reconciliation?',
      'How do I use the quiet roster header and controls?',
    ],
  },
  {
    prefix: '/settings/payroll/eba',
    questions: [
      'Why are manual fact values or the void review queue unavailable?',
      'Why did my mapping change not update an approved pay run?',
      'Why will Confirm not save my Xero earnings rate?',
      'How do I map Enterprise Agreement components to Xero?',
      'How do I push a pay run to Xero?',
    ],
  },
  {
    prefix: '/roster/leave',
    questions: [
      'How is this leave paid?',
      'How do I submit leave for a recorded absence?',
      'How do I review a leave request and use Status or More?', // timesheets-and-leave: New request and Submit request.
      'Why can’t I approve these leave hours?',
      'How do I retry a leave policy check?',
      // Includes worker/date eligibility and casual paid-leave refusal guidance.
      'Why is leave availability unavailable?',
      // New workflows retain the established six visible questions.
      'On /roster/leave, how do meal breaks affect leave hours?',
      'Why could the roster overlap not be checked?',
      'Why must I refresh or contact a coordinator when submitting absence leave?',
      'Why does a leave request need platform attention?',
      'Why is Refresh hours unavailable on this leave request?',
      'What does Settled — paid before Statura leave went live mean?',
      'How do I bulk approve leave requests?',
      'How do I approve leave over a worked timesheet?',
      'Why was leave refused or flagged for overlap?',
      'How do I change approved leave that was sent to Xero?',
      'What does Finish this leave change do?',
      'Why does Amend offer only some leave types?',
      'Why can I not assign leave on the absence plane?',
      'How do I release rostered shifts after approving leave?',
      'Was the worker told about my leave decision?',
      'Why do names from a Master appear on leave overlap?',
      'How do I attach leave evidence to a request?',
      'How do I view leave balance as at the last sync, and why is long service leave indicative?',
      'How do I accept roster-history evidence for leave?',
      'How do I re-record a missing leave approval record for payroll evidence?',
      'How do I refresh leave balances from Xero?',
      'How do I request a cash-out or termination leave payout?',
      'How do I approve timesheets?',
      'How do I publish the roster?',
      'Can we manage leave before enrolling our first client?',
      'How do I use the quiet roster header and controls?',

      // Includes neutral dated notices and recorded single-officer approval on the leave question.
      'How do I confirm public holiday pay during leave?',
      'Why could my approved leave change not be submitted?',
    ],
  },
  {
    prefix: '/me/leave',
    questions: [
      'How do I request leave?',
      'Why is leave availability unavailable?',
      'Why can’t I submit these leave hours?',
      'Why is my leave policy check unavailable?',
      'How do I submit leave for a recorded absence?',
      'How do I request leave from an absence link?',
      'Why must I refresh or contact a coordinator when submitting absence leave?',
      'How do I attach leave evidence?',
      'Why can I only choose some leave types when I change my leave?',
      'How do I view my leave balance as at the last sync, and why is long service leave indicative?',
      'Who approved or declined my leave request?',
      'How do I view my roster?',
      'How do I review my hours?',
      'Why is there no personal leave to show?',
    ],
  },
  {
    prefix: '/workforce/handovers',
    questions: [
      'How do I create a shift handover?', // workforce-handovers: Create handover, Outgoing worker and Incoming worker.
      'What if no incoming worker is assigned?',
      'What if my mobile handover is saved but finishing fails?',
      'Who needs to acknowledge a handover?',
    ],
  },
  {
    prefix: '/clinical/handovers',
    questions: [
      'How do I create a shift handover?', // workforce-handovers: Create handover, Outgoing worker and Incoming worker.
      'What if no incoming worker is assigned?',
      'What if my mobile handover is saved but finishing fails?',
      'Who needs to acknowledge a handover?',
      'How do I find the right client in a shared picker?',
    ],
  },
  {
    prefix: '/roster/handovers',
    questions: [
      'How do I create a shift handover?', // workforce-handovers: Create handover, Outgoing worker and Incoming worker.
      'What if no incoming worker is assigned?',
      'What if my mobile handover is saved but finishing fails?',
      'Who needs to acknowledge a handover?',
      'How do I use the quiet roster header and controls?',
      'How do I use Mine, Missing owner and overdue handovers?',
    ],
  },
  {
    prefix: '/roster/dispatch/print/handouts',
    questions: [
      'How do I print all worker roster handouts?', // roster-management: Worker roster and Roster print — By worker.
      'How do I publish the roster?',
      'Where is the roster board?',
      'How do I use the quiet roster header and controls?',
    ],
  },
  {
    prefix: '/roster/dispatch/print',
    questions: [
      'Why could the roster print preview not load?',
      'How do I print all worker roster handouts?', // roster-management: Worker roster and Roster print — By worker.
      'What should I do when roster leave evidence is unavailable?',
      'How do I use the quiet roster header and controls?',
    ],
  },
  {
    prefix: '/home-care/external-roster-import',
    questions: ['Where do I import a new provider roster with explicit programme binding?'],
  },
  {
    prefix: '/roster/import',
    questions: [
      'How do I import a roster with explicit programmes and review held or updated rows?',
      'Can I import a roster when a worker cannot be matched?',
      'Are unmatched shifts with the same source parent combined?',
      'How do I import a generic roster CSV?',
      'What are the generic roster CSV file limits?',
      'How do I import a provider roster?',
      'What does provider-policy impact mean?',
      'How do I publish the roster?',
      'How do I use the quiet roster header and controls?',
    ],
  },
  {
    prefix: '/roster/dispatch',
    chspQuestions: ['How do I schedule a CHSP recurring visit from a client record?'],
    byProgram: {
      RAC: [
        'How do I reschedule, duplicate or make a shift recurring from Dispatch?',
        'Why are care minutes unassessable in roster coverage or audit evidence?',
        'I am on /roster/dispatch. How do I add a staff shift when client visits are read-only?',
        'How do I give workers instructions for one shift?',
        'How do I retry the leave policy check for a shift?',
        'What should I do when roster leave evidence is unavailable?',
        'Why does a worker credential finding not show on the roster card?',
      ],
      SAH: [
        'How do I reschedule, duplicate or make a shift recurring from Dispatch?',
        'I am on /roster/dispatch. How do I add a staff shift when client visits are read-only?',
        'How do I give workers instructions for one shift?',
        'Can I assign a short-rest override before pay setup is complete?',
        'How do I retry the leave policy check for a shift?',
        'What should I do when roster leave evidence is unavailable?',
        'Why does a worker credential finding not show on the roster card?',
      ],
      OTHER: [
        'How do I dispatch an unfilled shift?',
        'Can I roster home-care clients with different funding programmes together?',
        'How do I repeat CHSP visits without a care plan?',
        'How do I repeat NDIS visits without a care plan?',
        'How do I repeat Private visits without a care plan?',
        'Where do I edit repeating visits and link a care plan?',
        'Why did Generate Visits leave a repeating visit open instead of assigning the preferred worker?',
      ],
    },
    questions: [
      'I am on /roster/dispatch. How do I add a staff shift when client visits are read-only?',
      // The answer includes the roster-slot requirement and slot-less parent guidance.
      'How do I give workers instructions for one shift?',
      'How do I dispatch an unfilled shift?',
      'Can I assign a short-rest override before pay setup is complete?',
      'How do I retry the leave policy check for a shift?',
      'What should I do when roster leave evidence is unavailable?',
      'How do I schedule care for a planned Support at Home client in Dispatch?',
      'How do I add a shift to an already-published roster?',
      'How do I view the roster from a specific date?',
      'How do I search for a worker in By worker view?',
      'Why does a shift show Assigned worker (details unavailable)?',
      'Can I roster home-care clients with different funding programmes together?',
      'How do I retry worker search or refine more than 50 matches?', // Roster Management: nested picker Escape and focus return.
      'How do I record a declined cover call?',
      'How do I see who has already been contacted for this shift?',
      'How do I review assignment after a worker accepts verbally?',
      'How do I keep the same period between Schedule and Time & closeout?',
      'How do I hand a started shift over to another worker?',
      'How do I add another worker to a started shift?',
      'How do I finish a started shift early because of sickness or injury?',
      'How do I change duty, client or pay facts after a shift has started?',
      'How do I record an absence and find cover for one shift?',
      'What should I do when an absence case changed?',
      "When should I use Book leave vs Can't attend / find cover?",
      'How do I print all worker roster handouts?', // roster-management: Worker roster and Roster print — By worker.
      'How do I publish the roster?',
      "How do I run today's roster?",
      'Where is the roster board?',
      'How do I show a fortnight on the roster?',
      'How do I set up the first roster?',
      'Why does Dispatch name a Master roster on a draft?',
      'Can we roster worker shifts before enrolling our first client?', // roster-management: confirmed-empty onboarding; Create staff shift; Review & publish worker roster.
      'What if no roster period covers my chosen date?',
      'Where are Handovers and Open shifts on the Cover & attendance landing?',
      'How do I create repeating home-care visits without a care plan?',
      'I am on /roster/dispatch. How do I change draft shifts together and recover an interrupted batch?',
      'What does the roster items to review count on Dispatch mean?',
      'How do I change shift times or repeat a shift next week?',
      'On Dispatch, how do I cancel a shift and check the assigned workers?',
      'How do I use the Roster header Exceptions button and return to the full-width planner?', // Single checks entry; Filters/More are documented in roster-management.
      'How do I choose the right time during a clock change in Dispatch?',
      'On /roster/dispatch, how do default meal breaks and zero minutes work?',
      'On /roster/dispatch, which meal break rule applies to this shift?',
      // Questions new in Direction D append after the established order.
      'How do I reschedule, duplicate or make a shift recurring from Dispatch?',
      'I am on /roster/dispatch. How do I use More, Exceptions and empty-week actions?',
      'I am on /roster/dispatch. Why can I draft an assignment for a worker who is not roster-ready?',
      'How do I adjust the Roster board and open the full-screen planner?', // Includes deferred health/coverage and local roster layout choices. // All planner states: publication, Tools, protected exceptions and collapsed runs counts.
      'Why is my roster read-only?',
      'How do I keep scheduling a legacy mixed period?',
      'When do unfilled roster alerts appear?',
      'Which roster grid opens by default?',
      'How do I open a bookmarked shift?',
      'On /roster/dispatch, why must I choose a client before assigning a visit worker?',
      'Why are care minutes unassessable in roster coverage or audit evidence?',
      'Which funding stream does a Support at Home visit draw from?',
      'How do I roster a split visit for a client without a care plan?',
      'Why did I receive one worker assignment alert for several shifts?',
      'How do I show two weeks on the Roster board?',
      'Why does the shift drawer show client and role-check context?',
      'Why does Plan repeating visits say programme details changed while the form was open?',
      'Why did Generate Visits not create a repeating visit for today or an earlier date?',
    ],
  },
  {
    prefix: '/roster/board',
    questions: [
      'Where is the roster board?',
      'How do I dispatch an unfilled shift?',
      "How do I run today's roster?",
      'Can I assign a short-rest override before pay setup is complete?',
      'How do I use the quiet roster header and controls?',
    ],
  },
  {
    prefix: '/roster/grid',
    questions: [
      'Where is the roster grid?',
      'How do I dispatch an unfilled shift?',
      "How do I run today's roster?",
      'How do I use the quiet roster header and controls?',
    ],
  },
  {
    prefix: '/roster/time-review',
    questions: [
      'How do I confirm the day?',
      'What does SAH visits still open count?',
      'When do timesheets start after a Master roster is applied?',
      "How do I run today's roster?",
      'How do I approve timesheets?',
      'How do I use the quiet roster header and controls?',
      'How do I approve hours straight after confirming rostered time?',
    ],
  },
  {
    prefix: '/roster/confirm-day',
    questions: [
      'How do I confirm the day?',
      "How do I run today's roster?",
      'How do I approve timesheets?',
      'How do I use the quiet roster header and controls?',
    ],
  },
  {
    prefix: '/roster/today',
    questions: [
      "How do I run today's roster?",
      'Where is the roster board?',
      'How do I confirm the day?',
    ],
    byRole: {
      care_worker: CARE_WORKER_ROSTER_QUESTIONS,
      clinician: CLINICIAN_ROSTER_QUESTIONS,
    },
  },
  {
    prefix: '/roster/live',
    questions: [
      'How do I monitor live shifts?',
      'How do I dispatch an unfilled shift?',
      "How do I run today's roster?",
      'How do I use the quiet roster header and controls?',
    ],
    byRole: {
      care_worker: CARE_WORKER_ROSTER_QUESTIONS,
      clinician: CLINICIAN_ROSTER_QUESTIONS,
    },
  },
  {
    prefix: '/roster/runtime',
    questions: [
      'How do I manage a live shift?',
      'How do I record a worker’s attendance when they cannot use the app?',
      'How do I dispatch an unfilled shift?',
      'How do I resolve a missed visit?',
      'How do I use the quiet roster header and controls?',
    ],
    byRole: {
      care_worker: CARE_WORKER_ROSTER_QUESTIONS,
      clinician: CLINICIAN_ROSTER_QUESTIONS,
    },
  },
  {
    prefix: '/roster/sleepover-disturbances',
    questions: [
      'How do I log a sleepover disturbance during a daylight-saving change?',
      'How do I review a sleepover disturbance event?',
      'What is the difference between a sleepover allowance and a disturbance?',
      'Why does an approved disturbance still show unsettled interval evidence?',
      'What do recorded time evidence and the flat sleepover allowance mean?',
      'How do I use the quiet roster header and controls?',
    ],
  },
  {
    prefix: '/roster/recalls',
    questions: [
      'How do I log a recall during a daylight-saving change?',
      'How do I review a recall-to-work event?',
      'How do I correct a recall decision?',
      'Why is payroll holding an approved recall for calculation context review?',
      'Can payroll proceed with a legacy recall rate warning?',
      'Why is an approved recall not marked paid?',
      'How does recall approval calculate pay?',
      'How do I use the quiet roster header and controls?',
    ],
  },
  {
    prefix: '/roster/continuity',
    questions: [
      'How do I read client continuity counts and open a client’s schedule?',
      'Does Client continuity apply a continuity target?',
      'How do I filter Client continuity by worker count and review client visits?',
      'How do I use the quiet roster header and controls?',
    ],
  },
  {
    prefix: '/roster/replace',
    questions: [
      'How do I offer a shift to workers from Replacement shift?',
      'On /roster/replace, how do I pick a worker if ranking is unavailable?',
      'How do I search active workers when candidate review is unavailable?',
      'What is the difference between First accept and Manager approve for a replacement?',
      'Why is a replacement offer held and not sent to workers?',
      'How do I use the quiet roster header and controls?',
    ],
  },
  {
    prefix: '/roster/shift-swaps/new',
    questions: ['How do I request a shift swap on a worker’s behalf?'],
  },
  {
    prefix: '/roster/shift-swaps',
    questions: [
      'How do I review and approve a shift swap request?',
      'How do I record that two workers agreed to a swap?',
      'How do I use the quiet roster header and controls?',
    ],
  },
  {
    prefix: '/roster/proposals/anacc',
    questions: ['How do I apply an AN-ACC wing staffing proposal?'],
  },
  {
    prefix: '/roster/proposals/restrictive-practice',
    questions: ['How do I apply a restrictive practice wing staffing proposal?'],
  },
  {
    prefix: '/roster/proposals',
    questions: ['How do I apply a schedule proposal from a care plan?'],
  },
  {
    prefix: '/roster/unfunded-visits',
    questions: [
      'Why is a delivered visit listed in Unfunded visits?',
      'How do I link an unfunded visit to a funded inclusion?',
      'Where do I repair a missing inclusion for an unfunded visit?',
      'How do I use the quiet roster header and controls?',
    ],
  },
  {
    prefix: '/roster/visit-exceptions',
    questions: [
      'How do I resolve a visit exception?',
      'Why does a visit say Worker clocked out — confirm visit?',
      'Does Confirm attended approve the visit hours?',
      'How do I use the quiet roster header and controls?',
    ],
    chspQuestions: ['How do I record or release a CHSP service?'],
  },
  {
    prefix: '/roster/wing-staffing',
    questions: [
      'How do I set staffing demand for a wing and shift?',
      'Where do I find Propose from history, Columns and row More in Staffing demand?',
      'How do I repair only blocking staffing requirements?',
      'How do I use the quiet roster header and controls?',
    ],
  },
  {
    prefix: '/roster/templates',
    questions: [
      'How do I apply a Master roster to a draft period?',
      'How do I choose a draft period to update remaining shifts after editing a Master line?',
      'Does applying a Master roster publish it?',
      'How do I recover a saved or interrupted Master application?',
      'How do I open a Master roster by name and review its cycle and demand notice?',
      'Will Replace keep the lines already in the draft?',
      'Why can’t I apply this Master to a draft that already has shifts?',
      'What happens if a named worker cannot be assigned from a Master roster?',
      'What is the difference between a Master vacancy and a failed assignment?',
      'How do I create a Master roster and add or duplicate lines?',
      'How do I find and edit a Master roster line?', // Roster Management: Search default workers / Search agency providers.
      'How does the Master apply receipt show worker clashes and possible duplicate times?',
      'How do I choose the Master roster window and destination dates?',
      'How do I apply a Master roster from a draft period?',
      'When should I rebuild shifts created by a Master roster?',
      'How do I use the Master worker grid and 2 weeks view?',
      'How do I add a Master line from an empty worker cell?',
      'Does clearing Master line filters keep my cycle window and view?',
      'How do I change several Master lines together?',
      'Do the Master roster controls change when Worker grid is off?',
      'Why is the Master roster department filter hidden or unavailable?',
      'How do I search default workers before editing a Master roster line?',
      'Why does a Master roster shift start at a different time at another facility?',
      'On /roster/templates, how do default meal breaks and zero minutes work?',
      'On /roster/templates, which meal break rule applies to this shift?',
      'How do I use the quiet roster header and controls?',
      'How do I delete or restore a Master roster line?',
      'How do I take a worker off just one day of the Master?',
      'How do I change settings after Master Apply is refused?',
      'Why does a Master worker row say the worker has left?', // master-roster-planning: worker-left row note.
      'How do I print the Master grid?',
    ],
  },
  {
    prefix: '/roster',
    questions: [
      "How do I run today's roster?",
      'How do I dispatch an unfilled shift?',
      'How do I approve timesheets?',
      'How do I retry the leave policy check for a shift?',
      'How do I use the quiet roster header and controls?',
      'Why is my roster read-only?',
      'How do I keep scheduling a legacy mixed period?',
    ],
    byRole: {
      care_worker: CARE_WORKER_ROSTER_QUESTIONS,
      clinician: CLINICIAN_ROSTER_QUESTIONS,
    },
  },
  {
    prefix: '/payroll/corrections',
    questions: [
      'How do I correct an approved timesheet?',
      'Why is a worked-time correction held during a daylight-saving change?',
      'How do I correct pay identity after a timesheet entered a run?',
      'How does a reviewer know a correction is waiting?',
      'How do I correct an approved or paid timesheet?',
      'How do I push a pay run to Xero?',
      'How do I approve timesheets?',
    ],
  },
  {
    prefix: '/payroll/queue',
    questions: [
      'How do I search the active pay-run queue?',
      'How do I fix a pay run snapshot failure?', // Includes fully or partially released leave and complete snapshot recovery.
      'How do I correct an approved timesheet?',
      'How do I use the pay-run queue?',
      'How do I close this fortnight?',
      'How do I push a pay run to Xero?',
    ],
  },
  {
    prefix: '/payroll/provider-handoff',
    questions: ['How do I review payroll provider handoff?'],
  },
  {
    prefix: '/payroll/pay-runs',
    roleQuestions: [
      {
        questions: ['On /payroll/pay-runs, why is one worker’s salary on hold?'],
        requiresRoles: ['TENANT_ADMIN', 'FINANCE_OFFICER', 'REGION_FINANCE_OFFICER'],
      },
    ],
    questions: [
      'How do I create a regular or off-cycle pay run?',
      'Who can approve a payroll rebuild, and do retries change captured inputs?', // Includes accepted background launches, capture progress and same-request recovery.
      'Why does a paid-externally run have no pay item amounts?', // Answer owner: pay-runs-overview; external settlement evidence.
      'How do I download and print a weekly worker pay summary?',
      'What should I do if settled pay input access is denied?',
      'What should I do if a Xero timesheet send needs recovery?',
      'Why are these pay lines held by a payroll file claim?',
      'How do I search all pay-run history or clear an empty view?',
      'Where do I review pay lines and add a provider adjustment?',
      'Where are optional provider items and a linked correction row?',
      'How do I recover when preparing handoff evidence asks for a refresh?',
      'How do I open an older correction from a review link?',
      'When do I re-seal a pay-run snapshot?',
      'When can I finalise a regular pay run?',
      'What should I read before export attestation?',
      'When can I mark a pay run as Paid?',
      'How do I verify an ABA bank payment?',
      'How do I authorise an expired screening payroll exception?',
      'Why does a pay line say Daylight saving start?',
      'Why is a client service-area lookup holding my pay run?', // Includes payable cancellations and safe snapshot retry.
      'How do I fix a pay run snapshot failure?', // Includes fully or partially released leave and complete snapshot recovery.
      'Why does a snapshot blocker show recovery guidance instead of a record ID?',
      'How do I push a pay run to Xero?',
      'How do I write priced payslips to Xero?',
      'How do I reconcile payslips after posting in Xero?',
      'What does Resume write do on the payslip panel?',
      'Why is my Xero pilot comparison certified or refused?',
      'How do I correct an approved timesheet?',
      'I am on /payroll/pay-runs. Why is a rebuild earnings line held before sending to Xero?',
      'On /payroll/pay-runs, why is one worker’s salary on hold?',
      'On /payroll/pay-runs, why is this timesheet held after approval?',
      'On /payroll/pay-runs, why did payroll use the direct care cost centre?',
      'I am on /payroll/pay-runs. How does the two-day Xero rebuild plan protect call capacity?',
      'I am on /payroll/pay-runs. How does Day A leave reach Xero and Day B reconcile without duplicates?',
      'I am on /payroll/pay-runs. What should I do when the secure payroll connection is unavailable?',
      'I am on /payroll/pay-runs. Why are secure payroll permissions holding a Xero push?',
      'On /payroll/pay-runs, how do I change the Xero schedule?',
      'On /payroll/pay-runs, how do I return a period to the old payroll?', // Answer owner: pay-runs-overview; Return a period to the old payroll.
      'On /payroll/pay-runs, how do I enable and recover a payroll rebuild?', // Answer owner: pay-runs-overview; officer rebuild journey. Preserve the established first six questions.
      'On /payroll/pay-runs, what does Break presumed from roster mean?', // pay-runs-overview
    ],
  },
  {
    prefix: '/roster/forecast',
    questions: ['Why does Cost forecast show priced subtotals and held leave?'],
  },
  {
    prefix: '/payroll/readiness',
    roleQuestions: [
      {
        questions: [
          'How do I review an absence case from Payroll > Readiness?',
          'Why does Sync pay context skip approved timesheets?',
        ],
        requiresRoles: ['PAYROLL_OFFICER'],
      },
    ],
    questions: [
      'How do I fix a pay run snapshot failure?',
      'How do I keep native Xero loading and record only the annual leave shortfall for this pay period?',
      'In Payroll > Readiness, how does a tenant administrator enable leave correction writes?',
      'In Payroll > Readiness, how do I check Leave setup and review template repair proposals?',
      'In Payroll > Readiness, do Leave setup warnings stop pay and how do I fix them in Xero?',
      'In Payroll > Readiness, how do I review Leave balance corrections and export a pay-run summary?',
      'In Payroll > Readiness, how does additional shift-worker leave qualification work?',
      'How do I repair missing or outdated pay roles for a past pay period?',
      'In Payroll > Readiness, how do I confirm or adjudicate annual leave loading closeout?',
      'Why does Sync pay context skip approved timesheets?',
      'Why is a worker blocked by an undecided worked-time review?',
      'How do I confirm or revoke the facility for leave in Payroll > Readiness?',
      'Why is leave held separately in snapshot rehearsal and completion?',
      'How do I review an absence case from Payroll > Readiness?',
      'What does Accrual check on Payslip leave mean?',
      'Why does an approved timesheet show a fact-set review or re-approval hold?',
      'Where do I fix a worker blocking award review?',
      'What does Rehearse snapshot (dry run) check?',
      'Why can coordinators, facility managers and team leaders see held-out payroll sources?',
      'What is a Soft advisory blocker on payroll readiness?',
      'What are Pay calculations needing attention on payroll readiness, and is it safe to stop retrying them?',
      'On /payroll/readiness, how do I use paged payroll problems?', // Flag-on paged reads; kept below the established first six.
      'What is Absences without leave on payroll readiness?',
      'How do I record Xero payslip leave without pushing a batch?',
      'Why did approved leave in a posted Xero period stay for payroll review?',
      'Why is Statura proposing extra annual leave for a worker?',
      'Why was my leave entitlement proposal check deferred?',
      'How does Xero-first leave get recorded overnight?',
      'How do I fix a paid-timesheet leave collision?',
      'How do I download leave evidence for a pay period?',
      'How do I download timesheet corrections evidence for a pay period?',
      'How do I correct an approved timesheet?',
      'How do I retry failed pay processing?',
      'What should I do when pay processing checks cannot load?',
      'Why do time and pay checks need review before export?',
      'How do I recover from a leave evidence refusal on payroll readiness or snapshot dry run?',
      'How do I clear a short-rest payroll hold after setting the missing pay profile?',
      'Why is automatic annual leave loading on hold when I write payslips?',
      'What does Delivered by Statura mean for my annual leave loading?',
      'How do I check and record the annual leave loading shortfall for this pay period?',
      'In Payroll > Readiness, why does additional shift-worker leave need a provider decision?',
    ],
  },
  {
    prefix: '/payroll',
    questions: [
      'How do I follow payroll steps with a manual provider?',
      'On /payroll, how do I check payroll status and fix held timesheets?',
      'Why is pay context unavailable in a timesheet breakdown?',
      'How do I keep native Xero loading and record only the annual leave shortfall for this pay period?',
      'In Payroll > Readiness, how do I check Leave setup and review template repair proposals?',
      'How do I close this fortnight?',
      'How do I use the pay-run queue?',
      'How do I push a pay run to Xero?',
      'Where do I download the pay-run CSV?',
      'What is Absences without leave on payroll readiness?',
      'How do I download leave evidence for a pay period?',
      'How do I correct an approved timesheet?',
      'How do I connect Xero payroll?',
      'Can we open payroll before enrolling our first client?',
      'How do I check and record the annual leave loading shortfall for this pay period?',
      'On /payroll, how do I resume sending unsent payslips?',
      'On /payroll, what happens if I close the page while a payroll task is running?',
      'On /payroll, how do I cancel, correct or replace a pay run?',
      'On /payroll, can I pay the verified lines while other items are held?',
      'On /payroll, how do I return a period to the old payroll?', // Answer owner: pay-runs-overview; Return a period to the old payroll.
      'On /payroll, how do I use Payroll Work?',
      'On /payroll, how do I check Payroll setup?', // Answer owner: pay-runs-overview; effective approval policy and period shift pay roles.
      'On /payroll, how do I review one-off and hand-keyed amounts?',
      'On /payroll, why is captured rebuild or workers compensation pricing on hold?',
      'On /payroll, how do I review worker pay lines and the updated Xero plan?', // Answer owner: pay-runs-overview; Officer rebuild journey.
      'On /payroll, what happens when the automatic Xero check fails?', // Answer owner: pay-runs-overview; Officer rebuild journey.
      'On /payroll, why is Xero preparation unavailable?',
      'When can I finalise a regular pay run?',
      'On /payroll, what do I do for the first pay run?', // Answer owner: payroll-work.
      'On /payroll, how can one person approve pay and the Xero plan?', // Answer owner: payroll-work.
      'On /payroll, how do I check rostered breaks?', // Answer owner: payroll-work.
      'On /payroll, how do I fix held pay?', // Answer owner: payroll-work.
      'On /payroll, how do I send and check payslips?', // Answer owner: payroll-work.
      'On /payroll, how do I change a pay run?', // Answer owner: payroll-work.
      'On /payroll, where do I review time and pay setup?', // Answer owner: payroll-work.
    ],
  },
  {
    prefix: '/payroll/setup',
    questions: [
      'Where can I see every rostered worker with incomplete pay set-up?',
      'Why does Payroll home show Finish payroll setup?',
      'How do I complete the readiness dry run step?',
      'Where do I map Xero earnings for an enterprise agreement?',
    ],
  },
  {
    prefix: '/payroll/policy',
    questions: [
      'How do I approve a payroll policy?',
      'Can a payroll policy start before today?',
      'Does the part-time ordinary-pattern exception prove the patterns are right?',
      'How do I set the overtime averaging cycle?',
      'Can a worker take annual leave instead of public holiday pay?',
    ],
  },
  {
    prefix: '/settings/roster',
    questions: [
      'What does each worker-compliance enforcement mode block?',
      'How do I use roster settings by provider intent?',
      'Why is a roster rule fixed or Unknown?',
      'When are Why explanations available for roster rules?',
      'How do I preview a provider roster preset?',
      'How do I preview provider operational controls?',
      'How do I change original recall approval authority?',
      'How do I publish the roster?',
      'I am on /settings/roster. Why is my Settings save refused after access changes?',
      'Why can’t I change roster adoption mode?',
    ],
  },
  {
    prefix: '/settings/integrations/xero/wizard/account-codes',
    questions: [
      'How do I map residential fee components to Xero accounts?',
      'How do I map DAP, DAC and bed reservation fees separately?',
    ],
  },
  {
    prefix: '/settings/integrations/xero/wizard',
    questions: [
      'I am on /settings/integrations/xero/wizard. How do I refresh Xero accounting Items for a chosen connection?',
      'I am on /settings/integrations/xero/wizard. Why is there a Duplicate Xero earnings-rate mapping blocker?',
      'What happens when the Xero connection changes during a refresh?',
      'How do I re-sync Xero pay templates now?',
      'Why is Xero readiness shown as of an earlier sync?',
      'Why are some Xero employee details still incomplete after re-sync?',
      'How do I confirm Xero earnings-rate mappings?',
      'I moved an employee to another pay calendar in Xero — what do I do in Statura?',
      'Which clients, programmes and staff are not linked to my accounting system?',
    ],
  },
  {
    prefix: '/settings/integrations/xero',
    questions: [
      'Why does Xero ask me to wait for call capacity?',
      'How do I connect Xero payroll?',
      'How do I push a pay run to Xero?',
      'How do I approve timesheets?',
      'Why does Xero API usage show an older remaining-call figure?',
    ],
  },
  {
    prefix: '/me/onboarding/financial',
    questions: [
      'I am on /me/onboarding/financial. Who can see my tax declaration?',
      'I am on /me/onboarding/financial. Why are my saved tax answers unavailable?',
      'How do I complete my payroll financial details?',
    ],
  },
  {
    prefix: '/me/onboarding',
    questions: [
      'I am on /me/onboarding. Why do I see Waiting on your manager in My onboarding?',
      'I am on /me/onboarding. Can I sign part of my document bundle?',
      'I am on /me/onboarding. How do I continue the exact task in the worker app, including after sign-in or offline?',
      'I am on /me/onboarding. How do I opt in to payroll task links on web?',
      'I am on /me/onboarding. Who owns my remaining steps and when are they due?',
      'I am on /me/onboarding. How do I continue my stalled onboarding journey?',
      'How do I complete onboarding documents?',
      'How do I acknowledge policies?',
      'How do I view my credentials?',
      'I am on /me/onboarding. How do I declare Australian citizenship for my first shift?',
      'I am on /me/onboarding. Does saving my personal details send them for review?',
    ],
  },
  {
    prefix: '/people/offboarding',
    questions: [
      'I am on /people/offboarding. Which structured exit reason should I choose, and when is Other required?',
      'Why is Amend unavailable on an offboarding journey?',
      'I am on /people/offboarding. Which checklist steps can I waive with a reason?',
      'I am on /people/offboarding. What should I record for a responsible person leaving?',
      'I am on /people/offboarding. How do I review terminated-worker leave exceptions and Xero payroll commands?',
      'I am on /people/offboarding. How do I reconcile a command after its in-flight lease expires?',
      'I am on /people/offboarding. How do I revoke a worker’s access immediately for a security case?',
      'I am on /people/offboarding. How do I restore access after an emergency revocation?',
      'I am on /people/offboarding. How do I review final pay, understand provider rule holds, refresh stale decisions, and certify the leave snapshot?',
      'I am on /people/offboarding. How do I save termination facts and rebuild a stale leave snapshot?',
      'How do I change or cancel a scheduled offboarding?',
      'When do access and employment end for a future final date?',
      'What happens to later shifts when I start offboarding?',
      'How do I record a resignation after the worker’s last day?',
      'What happens to access and the checklist when I record a late resignation?',
      'What happens when I resend an invitation more than once?',
      'Who can start an offboarding journey?',
      'I am on /people/offboarding. Why can’t I see or complete a worker’s offboarding journey?',
      'Why can the People summary and offboarding workbench show different counts?',
      'Why can’t I record a past final date while the worker still has rostered shifts?',
    ],
  },
  {
    prefix: '/people/onboarding/new',
    questions: [
      'How do I add a worker and confirm their engagement?',
      'How do I recover an interrupted worker invitation?',
      'How do HR Officers and HR Directors check pay setup for an employment agreement?',
      'Why can’t my role list onboarding candidates here?',
    ],
  },
  {
    prefix: '/people/onboarding',
    questions: [
      'I am on /people/onboarding. How do automatic onboarding reminders and overdue escalation work?',
      'I am on /people/onboarding. What do Protected and reminder delivery labels mean?',
      'I am on /people/onboarding. Why does a count show —?',
      'I am on /people/onboarding. Why did I receive one summary of overdue onboarding tasks, and where are tasks without an owner?',
      'I am on /people/onboarding. Who can use the onboarding queue?', // Includes Show all on this page and source-drawer closure.
      'I am on /people/onboarding. When can HR open restricted documents to verify them?',
      'I am on /people/onboarding. Can I be my own onboarding hiring manager as a sole operator?',
      'Why is an automatic employment agreement held?',
      'How do I retry automatic worker assignments?',
      'I am on /people/onboarding. What happens if induction assignment fails when I start a journey?',
      'I am on /people/onboarding. How do I review and close legacy idle onboarding journeys?',
      'I am on /people/onboarding. How do I hand off onboarding steps or change their deadlines?',
      'I am on /people/onboarding. What does Needs an owner mean on an onboarding step?',
      'I am on /people/onboarding. How do I continue a stalled onboarding journey?',
      'Where do I see onboarding work assigned to me?',
      'Which onboarding steps block journey completion?',
      'Can I reopen an onboarding step after the journey is complete?',
      'When can I waive an onboarding step?',
      'How do I set or change a Collect by date?',
      'How do I verify worker onboarding documents?',
      'How do I add a worker?',
      'What if a worker’s date of birth is unknown?',
      'How do HR Officers and HR Directors check pay setup for an employment agreement?',
      'What does Onboarding overdue mean?',
      'I am on /people/onboarding. Why can’t I roster this new worker?',
      'I am on /people/onboarding. Which checks are required by law before a first shift?',
      'I am on /people/onboarding. What do the onboarding requirement labels mean?',
      'How do I bring a journey up to date?',
      // Grouped queue and individual-check disclosure retain the existing workbench destinations.
      'How do I use the onboarding Work home and open the full workbench?',
      'I am on /people/onboarding. How do I approve or return personal details and record assisted capture?',
    ],
  },
  {
    prefix: '/hr/onboarding',
    questions: [
      'Where do I review a new worker’s pay setup?',
      'How do I verify worker onboarding documents?',
      'How do I add a worker?',
    ],
  },
  {
    prefix: '/people',
    questions: [
      'I am on /people. How do I resolve work without leaving Home?', // Includes Show all replacing the source drawer, ownership reset and renewal refusal guidance.
      'Why is a former worker shown as Unable to determine?',
      'I am on /people. How do I chase an overdue item?',
      'What does Workforce at a glance show?',
      // Work home: five risk-ordered groups, Show N more, unavailable checks and status chips.
      'On People > Home, how do I collapse or hide sections?', // Customise · N hidden, Undo, Reset layout; Needs action stays visible and protected rows collapse once.
      'Where do I review people lifecycle metrics?',
      'How do I use the People Work home?', // People breadcrumb and Calendar return context; answer owner people-hub.
      'How do I add a worker?',
      'Why can an established worker be Active without an onboarding journey?',
      'What does No current engagement mean?',
      'Does a missing app login stop a worker from working?',
      'How do I review onboarding and offboarding?',
      'How do I find who is blocked for a first shift?',
      'What do the 30-day and 60-day expiry counts include?',
      // Shared headline/drawer population, tenant-local overdue days, compact resolve rows and owner-follow-up destination.
      'What does Onboarding overdue mean?',
      'How do I find a worker?',
      'Where are the People family pages?',
      'How do I find who cannot work?',
      'What should I do if Upload on behalf cannot confirm the upload?',
      'How do I record First Aid or CPR with Upload on behalf?',
      'I am on /people. Why can I see only the worker list?',
    ],
  },
  {
    prefix: '/people/workers',
    questions: [
      'Why is a former worker shown as Unable to determine?',
      'Why is a worker shown as Awaiting onboarding?',
      'How do I recover an interrupted worker invitation?',
      'Who can capture HI.31 search sex on Worker 360?',
      'What is the difference between Not set and Not stated for search sex?',
      'Does turning off Healthcare Identifier integration stop care delivery?',
      'What does the worker assignment readiness reason mean?',
    ],
  },
  {
    prefix: '/home-care/statements',
    questions: [
      'Why are new-quarter agency budgets still missing, and how long must I wait between manual refreshes?',
      'How do I issue a Support at Home participant statement?',
      'What should I do when a statement is saved for manual delivery?',
      'What should I do if the Statements client list cannot load?',
      'How do I manage Support at Home budgets?',
      'What happens if scheduled statement checks fail?',
    ],
  },
  {
    prefix: '/home-care/budget-explainer',
    questions: [
      'How do I plan a provider-priced service mix with hardship-adjusted contributions and choose a planning quarter in the Support at Home budget explainer?',
    ],
  },
  {
    prefix: '/home-care/at-hm',
    // Preserve existing path questions; annual finance guidance follows them.
    conditionalQuestionsLast: true,
    conditionalQuestions: [
      {
        questions: [
          'Why are annual agency budgets not yet switched on?',
          'Where do I allocate an AT/HM request to its agency budget?',
          'How do I allocate and review an annual AT/HM agency budget?',
        ],
        requiresIntegration: 'services_australia',
        requiresRoles: [...SERVICES_AUSTRALIA_INTEGRATION_ROLES],
        requiredModules: ['/settings/integrations/services-australia'],
      },
    ],
    questions: [
      'How do I record AT-HM component prices and the written agreement?',
      'How do I manage Support at Home budgets?',
      'How do I prepare Support at Home claims?',
      'Why is an invoice line or claims CSV row on hold?',
    ],
  },
  {
    prefix: '/home-care/readiness',
    questions: [
      'How do I approve the provider foundation for go-live?',
      'How do I clear Support at Home delivery readiness?',
      'Where do I find Journey?',
      'How do I manage Support at Home budgets?',
      'How do I use the Journey tab?',
    ],
  },
  {
    prefix: '/home-care/exceptions',
    questions: [
      'How do I resolve a missed visit?',
      'Why is a cancellation saved with money held?',
      'Can I correct a visit longer than 12 hours to attended?',
      'How do I record a Support at Home visit?',
      'How do I clear Support at Home delivery readiness?',
    ],
  },
  {
    prefix: '/home-care/budgets',
    questions: [
      'Why are new-quarter agency budgets still missing, and how long must I wait between manual refreshes?',
      'How do I manage Support at Home budgets?',
      'How do I prepare Support at Home claims?',
      'Where do I find Journey?',
    ],
  },
  {
    prefix: '/home-care/claims/services-australia',
    requiresIntegration: 'services_australia',
    requiresRoles: [
      'OWNER',
      'SYSTEM_ADMIN',
      'TENANT_ADMIN',
      'COORDINATOR',
      'QUALITY_OFFICER',
      'COMPLIANCE_OFFICER',
      'FINANCE_OFFICER',
    ],
    questions: [
      'Why can I not preview a Support at Home invoice source?',
      'Why is an invoice line or claims CSV row on hold?',
      'Why can the sent invoice or claim behind a Services Australia finance read not be verified?',
      'How do I reconfirm submitted invoices or resolve a failed check before preparing a Support at Home claim?',
      'Why is NOI invoice recovery blocked?',
      'Why are some invoices not listed on Prepare?',
      'How do I check the outcome of a Support at Home claim without sending again?',
      'How do I check several uncertain Services Australia invoices?',
      'Can I review Services Australia invoice documents in the workbench?',
      'When can I refresh an agency invoice item before uploading approved documents?',
      'How does a different person approve or reject an invoice document?',
    ],
  },
  {
    prefix: '/home-care/claims',
    // Keep the two established entry points ahead of enabled annual-finance guidance.
    conditionalQuestions: [
      {
        questions: [
          'How do I prepare Support at Home claims?',
          'Why is an invoice line or claims CSV row on hold?',
          'Why are annual agency budgets not yet switched on?',
        ],
        requiresIntegration: 'services_australia',
        requiresRoles: [...SERVICES_AUSTRALIA_INTEGRATION_ROLES],
        requiredModules: ['/settings/integrations/services-australia'],
      },
    ],
    questions: [
      'How do I prepare Support at Home claims?',
      'Why is an invoice line or claims CSV row on hold?',
      'Why must I regenerate an older Support at Home claim batch?',
      'How do I link an AT/HM service to its request and create its invoice draft?',
      'How do I record a late AT/HM claim item?',
      'How do I manage Support at Home budgets?',
      'How do I record a Support at Home visit?',
    ],
  },
  {
    prefix: '/home-care/service-delivery',
    questions: [
      // Shared route: preserve the existing Support at Home path-first question.
      'How do I record a Support at Home visit?',
      'How do I approve the billable quantity for a Support at Home visit?',
      'Can I record a service before Support at Home setup is complete?',
      'How are held services checked and released after setup is fixed?',
      'What happens if consent changes after a service has already been billed?',
      'How do I clock in from Service delivery?',
      'How do I record a CHSP service?',
      'Why was my CHSP service recorded with funding held?',
      'How do I resolve a missed visit?',
      'How do I manage Support at Home budgets?',
      'Can I record a service without a service area, and how do I assign it later?',
      'Why is a completed visit held for its care-plan date?',
      'How is a care-plan date hold released after activation?',
      'Why does Service delivery say visits are rostered but none are recorded?',
    ],
    chspQuestions: [
      'How do I record group attendance for a CHSP session?',
      'How do I record domestic assistance tasks?',
      'How do I record and follow up a CHSP client service request?',
    ],
  },
  {
    prefix: '/home-care/notes-review',
    questions: [
      'How do I review a client note?',
      'Why was my client note review refused?',
      'How do I allow self-review for client notes?',
    ],
  },
  {
    prefix: '/cm-tasks',
    questions: [
      'How do I log CM time?',
      'What if Save and submit is blocked?',
      'How does finance bill approved CM time?',
    ],
  },
  ...['/clients/new', '/waitlist', '/residential/admissions'].map((prefix) => ({
    prefix,
    // Residential intake pages always offer it; shared intake pages only for
    // providers that register residents.
    ...(prefix === '/residential/admissions'
      ? {}
      : {
          byProgram: {
            RAC: [RESIDENT_REGISTRATION_PROGRAM_QUESTION],
            MIXED: [RESIDENT_REGISTRATION_PROGRAM_QUESTION],
          },
        }),
    conditionalQuestions: [
      {
        questions: [
          'How do I review a B2G referral import?',
          'How are reviewed referral badges labelled?',
        ],
        requiresIntegration: 'dhda_b2g' as const,
        requiresRoles: [...B2G_VIEW_ROLES],
        requiredModules: ['/settings/integrations/dhda'],
      },
    ],
    questions: [
      prefix === '/clients/new'
        ? 'How do I add a new client?'
        : prefix === '/waitlist'
          ? 'How do I manage the waitlist?'
          : 'How do I admit a new resident?',
      ...(prefix === '/waitlist'
        ? ['Why is Admit client unavailable for a waitlist referral?']
        : []),
      ...(prefix === '/residential/admissions'
        ? [
            RESIDENT_REGISTRATION_PROGRAM_QUESTION,
            'How do I attach a paper agreement after creating an admission intake?',
          ]
        : []),
      ...(prefix === '/clients/new'
        ? [
            'How do I get started with CHSP?',
            'Can I set up a newly registered Support at Home client before classification is recorded?',
            'Can I start assessments while Support at Home setup is in progress?',
            'What happens when I select CHSP while registering a client?',
          ]
        : []),
    ],
  })),
  {
    prefix: '/clients/batch-validation',
    requiredModules: ['/settings/integrations/hi'],
    requiresIntegration: 'hi',
    requiresRoles: ['OWNER', 'SYSTEM_ADMIN', 'TENANT_ADMIN', 'COORDINATOR', 'CLINICAL_LEAD'],
    questions: [
      'How do I run batch IHI validation for multiple clients?',
      'What if batch IHI validation does not load?',
      'How do I reopen or refresh an IHI batch job?',
      'How do I capture or validate a client IHI?',
      'Why can HI lookup not search using demographics alone?',
      'Does missing HI setup stop care delivery?',
      'I am on /clients/batch-validation. How do I review the client cohort before starting HI.12?',
    ],
  },
  {
    prefix: '/clients',
    roleQuestions: [
      {
        questions: ['How do I review a client’s role in compliance events?'],
        requiresRoles: ['COMPLIANCE_OFFICER', 'QUALITY_OFFICER'],
      },
      {
        questions: ['How do I export case notes as a PDF for one client?'],
        requiresRoles: CARE_NOTE_EXPORT_ROLES,
      },
      {
        questions: ['How do I edit decision-makers from the People tab?'], // Answer owner: managing-client-records.
        requiresRoles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'COORDINATOR', 'CLINICAL_LEAD'],
      },
      {
        questions: ['How do I link a client to my accounting system?'], // Answer owner: xero-mapping-wizard.
        requiresRoles: INVOICING_LINK_ROLES,
      },
    ],
    byProgram: {
      RAC: [
        'How do I discharge a resident who has no bed recorded?',
        'Where do I record a resident’s discharge destination?',
        'How do I release billing holds for missing entry or discharge facts?',
        'How do I record a dietitian’s planned weight-loss plan?',
        'How do I record a doctor-diagnosed psychosis for QI?',
      ],
      MIXED: [
        'How do I discharge a resident who has no bed recorded?',
        'Where do I record a resident’s discharge destination?',
        'How do I release billing holds for missing entry or discharge facts?',
        'How do I record a dietitian’s planned weight-loss plan?',
        'How do I record a doctor-diagnosed psychosis for QI?',
      ],
    },
    conditionalQuestions: [
      {
        questions: ['How do I check the agency source before editing an imported client?'],
        requiresIntegration: 'dhda_b2g',
        requiresRoles: ['COORDINATOR', 'CASE_MANAGER', 'REGISTERED_NURSE'],
        requiredModules: ['/settings/integrations/dhda'],
      },
      {
        questions: [
          'How do I check Services Australia records for a Support at Home client?',
          'What does the Services Australia care recipient summary show?',
          'Where are the Services Australia capture and review controls?',
          'Can I prepare a Services Australia identity match without confirmed Medicare?',
          'How do I select the residential opt-in iTEST case?',
        ],
        requiresIntegration: 'services_australia',
        requiresRoles: [...SERVICES_AUSTRALIA_INTEGRATION_ROLES],
        requiredModules: ['/settings/integrations/services-australia'],
      },
      {
        questions: [
          'How do I record a Support at Home re-admission and prepare an entry?',
          'How do I activate a planned Support at Home programme?',
          'How do I record a short-term Support at Home pathway start?',
          'How do I correct a short-term pathway start date or category?',
          'How do I recover an accepted Support at Home iTEST entry?',
        ],
        requiresIntegration: 'services_australia',
        requiresRoles: ['OWNER', 'SYSTEM_ADMIN', 'TENANT_ADMIN', 'COORDINATOR'],
        requiredModules: ['/settings/integrations/services-australia'],
      },
      {
        // Care-entry questions stay ahead of finance holds on the Clients path.
        questions: [
          'Why are annual agency budgets not yet switched on?',
          'Why is an invoice line or claims CSV row on hold?',
          'How do I review an annual agency budget difference?',
          'How do I allocate and review an annual AT/HM agency budget?',
        ],
        requiresIntegration: 'services_australia',
        requiresRoles: [...SERVICES_AUSTRALIA_INTEGRATION_ROLES],
        requiredModules: ['/settings/integrations/services-australia'],
      },
      {
        questions: [
          'I am on /clients/[id]. How do I look up our GP clinic or pharmacy HPI-O?',
          'I am on /clients/[id]. How do I start an HI.12 check from this client record?',
        ],
        requiresRoles: [
          'OWNER',
          'TENANT_ADMIN',
          'SYSTEM_ADMIN',
          'COORDINATOR',
          'CASE_MANAGER',
          'CLINICAL_LEAD',
        ],
        requiresIntegration: 'hi',
        requiredModules: ['/settings/integrations/hi'],
      },
    ],
    questions: [
      'What should I do when another resident change is being saved?',
      // Declared for Help lockstep; visibility/order comes from payerReferenceQuestions.
      'Where do I record a head-provider client ID for Support at Home?',
      'How do I start a care plan?',
      'Who needs me today on the client list?',
      'How do I confirm a programme for a saved client record?',
      'How do I record RAC service agreement consent in Stay & agreements?',
      "How do I send a resident's bills to their representative?",
      'How do I record GST treatment for a resident fee agreement?',
      "How do I record a resident's Services Australia fee advice?",
      'How do I correct a fee advice?',
      // Declared here for Help lockstep; shown to finance roles through racAgreementQuestions.
      'Where can finance review RAC agreement evidence and refunds?',
      'Where can I see active restrictive practice for a Support at Home client?',
      'How do I retry clinical evidence and search older resident history?',
      'How do I review a client’s role in compliance events?',
      'When does a family signing link need reissue?',
      'Can I set up a newly registered Support at Home client before classification is recorded?',
      'In Clients, how do I record a family communication decision?',
      'How do I administer family access for a client?',
      'How do I show a care team member to family?',
      'How do I publish a health summary or activity to family?',
      'How do I share a care plan or document with family?',
      'How do I record a queued or failed copy due to a registered supporter?',
      'When is a document recorded as issued to the client?',
      'How do I share a personal update with family?',
      'How do I assign a family form?',
      'How do I give a family member access to a client’s information?',
      'Where do exited or deceased clients go?',
      'How do I exit a client from community services?',
      'How do I record that a client withdrew consent for family to see their information?',
      'How do I review the next two weeks?',
      'Why is Support at Home information unavailable after changing programme?',
      'What does the visit pack show?',
      'Where do I edit clinical facts?',
      'Where do I edit how we care?',
      'Where do I see who to call?',
      'Where do I edit GP or pharmacy?',
      'Where do I edit standing risk?',
      'Where do I edit pronouns or Indigenous status?',
      'Where is home access on the client record?',
      'Where does Open agreement take an Intake client?',
      'How do I return to my search after opening a record?', // managing-client-records: Opening a record and returning.
      'How do I capture or validate a client IHI?',
      'How do I run batch IHI validation for multiple clients?',
      'Why can HI lookup not search using demographics alone?',
      'What happens when client demographics change after IHI validation?',
      'How do I record an incident from a client record?',
      'Where do I find Journey?',
      'How do I clear Support at Home delivery readiness?',
      'How do I commence a Support at Home client under a head provider?',
      'How do I record an associated provider Support at Home lifecycle change?',
      'How do I record historical HCP closeout?',
      'How do I refresh a blocked CHSP funding change?',
      'How do I activate a resident whose residential enrolment is still planned?',
      'How do I report a SIRS incident?',
      'How do I prepare a DVA Community Nursing claim?',
      'How do I open client records when my provider offers both home and residential care?',
      'I am on /clients. What happens when I change a signer’s email?',
      'How do I read and refresh the residential Today panel?',
      'How do I retry a case note when saving was interrupted?',
      'What does Saved but not visible mean for a case note?',
      "Why does a new case note need today's date?",
      // First-envelope setup excludes the quarter immediately before the current one.
      'How do I set up a missing past Support at Home quarter for clients with no quarter budgets yet?',
      'Why may past quarter setup raise a budget variance alert?',
      'How do I search a multiword client name without losing spaces?',
      // Keep newer residential finance prompts after the established client-list entry points.
      'How do I release billing holds for missing entry or discharge facts?',
      'How do I record a social-leave opening for a resident?',
      'How do I attach care-plan PDFs to several clients?',
      // Client Documents tab: head-provider care plan and recommended home safety row.
      'How does a head provider’s care plan count as the support plan?',
      'Is a home safety risk assessment required for Support at Home?',
      'How do I confirm revoking consent?',
      'How does my programme workspace determine the client list?',
      'Why am I asked to choose a programme to view medications?',
    ],
    chspQuestions: [
      'Where do I see whether a CHSP client has contribution terms and a charging cap?',
      'How do I review a CHSP provider and client relationship?',
      'Does accepting an external CHSP care plan complete the A3 obligation?',
      'How do I respond to a CHSP portal care-plan review request?',
    ],
    byRole: {
      clinician: [
        'How do I record a medication administration?',
        'How do I retry clinical evidence and search older resident history?',
        'How do I report a SIRS incident?',
        'Where do I find Journey?',
        'How do I read and refresh the residential Today panel?',
        'How do I retry a case note when saving was interrupted?',
        "Why does a new case note need today's date?",
      ],
      care_worker: [
        'How do I record a Support at Home visit?',
        'How do I record an incident from a client record?',
        'How do I retry clinical evidence and search older resident history?',
        'How do I view my roster?',
        'Where do I find Journey?',
        'How do I read and refresh the residential Today panel?',
        'How do I retry a case note when saving was interrupted?',
        "Why does a new case note need today's date?",
      ],
    },
  },
  {
    prefix: '/tasks/service-execution-reconciliation',
    questions: [
      'How do I resolve rejected service evidence?',
      'Why can’t I accept this worker submission?',
      'What does superseded evidence mean?',
    ],
  },
  {
    prefix: '/tasks',
    conditionalQuestions: [
      {
        questions: [
          'Does a safety handoff mean the clinician accepted the finding?',
          'Why is my government integration task still open after I read the alert?',
        ],
        requiresIntegration: 'dhda_b2g',
        requiresRoles: [...B2G_VIEW_ROLES],
        requiredModules: ['/settings/integrations/dhda'],
      },
    ],
    questions: [
      'How do I use Needs action, task stages and Columns in Tasks?', // Deduplicated owners, source review verbs and shared footer.
      'How do I respond to an urgent notification task and check its deadline?',
      'How do I open a protected notification response from the care-worker app?',
      'How do I review an incident reporting notification task?',
      'How do I respond to a routine screening renewal reminder?',
      'How do I continue a limited Tasks inbox or start a workflow?',
      'Does completing a compliance task record the source duty?',
      'How do I complete a programme evidence review task?',
      'How do I open overdue work?',
      'How do I start my day on Home?',
      'How do I report a SIRS incident?',
      'How do I review a family service request?',
      'How do I complete a family request with manual confirmation?',
      'How do I review a submitted family form?',
      'How do I complete a clinical alert notification response?',
      'How do I complete a data breach notification response?',
      'How do I complete an outbreak notification response?',
    ],
  },
  {
    prefix: '/compliance/calendar',
    questions: [
      'Which annual complaints reports and provider duty reminders appear in the calendar?',
      'What is the Compliance calendar?',
      'What do the This month cards on the Compliance calendar show?',
      'Where do open disclosure provider targets appear in My compliance work and calendar?',
      'How do I use the Compliance calendar agenda for deadlines?',
      'How do Me and Unassigned work in the Compliance calendar?',
      'How do I subscribe in my calendar?',
      'Why does the calendar link not show record details?',
    ],
  },
  {
    prefix: '/compliance/open-disclosure',
    questions: [
      'How do I record open disclosure for each person, and what if a save is not confirmed?',
      'How do I record consent and the person leading open disclosure?',
      'Where do open disclosure provider targets appear in My compliance work and calendar?',
    ],
  },
  {
    prefix: '/compliance/incidents',
    questions: [
      // Correction help includes original deadlines and persistent Timeline reminder review warnings.
      'How do I edit or correct a care incident?',
      'Where can I see reminder review warnings after reloading an incident?',
      'Can I open an incident link using its reference number?',
      // Includes Cancel confirmation and same-tab draft resume/discard.
      'How do I get back to the incident register or report another incident?',
      'How do I retire a person recorded in error?',
      'How do I filter care incidents or find new incidents since I last looked?',
      // Keep the established six suggestions first; these remain available to local Help/search.
      'Where are the incident summary and export options?',
      'Where can I see who was told about an incident and what is still waiting?',
      'What if my care incident save cannot be confirmed?',
      'What does Recorded mean on a care incident?',
      'How do I open a corrective action task from an incident?',
      'How do I review an earlier incident information requirement?',
      'How do I add an agency person to a saved incident?',
      'How do I add someone not in Statura to a saved incident?',
      'Where can I see a fall observation or medication subtype in the register?',
      'How do I write a correction reason on several lines?',
      'How do I record the home for an incident?',
      'How do I correct the hospital facts on a saved incident?',
      'How do I save the assessment and each affected person’s involvement?',
      'How do I record a witness’s involvement on an incident?',
      'Do I need a reason to remove a hazard link from an incident?',
      'How do I link a submitted follow-up report to an incident correction or information requirement?',
      'How do selected incident exports differ from header exports?',
      'What do care incident severity levels mean?',

      'How do I use the incident steps and Open items?',
      'Why are incident assessment and involvement not confirmed?',
      'Who can update, close or reopen an incident?',
      'What should I do if incident details cannot be loaded?',
      'Why is incident history limited or unavailable?',
      'Where does Add to risk register appear and what does it do?',
      'How do I collapse or hide the incident standards and timeline sections?',
      'Where do I record person notification for a non-reportable incident?',
      'Can a Compliance Officer print the incident audit pack or add notes?',
      'Why is a prevention evidence document unavailable?',

      'What should I do if an Accepted or Rejected SIRS regulator outcome cannot be confirmed?',
      'Where do I find the stable reference for an incident?',
      'Where do open disclosure provider targets appear in My compliance work and calendar?',
      'What does outside the Aged Care Act mean on an incident?',
      'Why are TAC and DVA contract reminders hidden on my incident?',
      'What does no programme attribution recorded mean on an incident?',
      'What does Programme not yet chosen mean on an incident?',
      'How do I record NDIS awareness and manual evidence separately from SIRS?',
      'How do I confirm a TAC or DVA contract reminder?',
      'How do I revise an open TAC or DVA reminder after terms change?',
      // Includes the seven-role self-investigation exception and recorded evidence.
      'Who appears in the Assign Investigator list on an incident?',
      'How do I open a breach from another event?',
      'How do I triage emergency or outside-plan restrictive practice use for SIRS?',
      'How do I complete or correct the SIRS notice and record who was notified?',
      'How do I decide whether open disclosure is needed for a non-SIRS incident?',
      'How do I triage an Other incident or correct a SIRS priority fact?',
      'How do I record a witness who is not in Statura?',
      'How do I link a staff member or care recipient to an incident?',
      'What if care recipient search is unavailable?',
      'How do I record effects or notifications for an involved person?',
      'Who can correct, close or reopen a care incident?',
      'How do I record someone not yet in Statura?',
      'How is gender recorded on an incident?',
      'How do I correct the person an incident was recorded against?',
      'What happens to family notifications and linked notes when I correct the person?',
      'What does Not yet assessed severity mean?',
      'How do I record the outcome of a fall for each person?',
      'How do I find falls needing outcome review?',
      'How do I record the Consider SIRS reasoning?',
      'Does entering a historic incident notify the Commission?',
      'I entered incidents before the ‘entered after the event’ section existed: how do I add the other system’s record number?',
      'What does Historic, not yet evidenced mean on a task?',
      'How do I record a body part, nature, mechanism and agency for an injury?',
      'What do the injury columns in the export mean?',
      'Which TOOCS edition are injuries coded to?',
      'What if involved people are unavailable on an incident?',
      'What are the SIRS Priority 1 and Priority 2 notification windows?',
      'How do I report a SIRS incident?',
      'Where do I find Journey?',
      'How do I record a medication administration?',
      'How do I link a WHS incident and incident for the same event?',
      'How are incident badges labelled?',
      'How do I finish reports from the one-door form?',
      'How do I reopen a dismissed pending report?',
      'Why is there no deadline on Tell the agency about this incident?',
      'Where is source-person follow-up managed after a needle-stick?',
      'Who receives an urgent possible Priority 1 alert?',
      ...ONE_DOOR_QUESTIONS,
      'How do I find the right client in a shared picker?',
      'Which form opens when I report from the care incident register?',
      'How do I record an agency worker or actions to prevent a repeat?',
      'Why was my SIRS priority override refused?', // Answer owner: incident-reporting-sirs.
      'How do I record the staff SIRS decision for an engine-reportable incident?', // Answer owner: incident-reporting-sirs.
      'Why have repeat SIRS scope-review reminders stopped?', // Answer owner: incident-reporting-sirs.
      'What changes when I save corrected SIRS facts?', // Answer owner: incident-reporting-sirs.
    ],
  },
  {
    prefix: '/compliance/complaints',
    questions: [
      'How do I submit a portal complaint without knowing the programme?',
      'I am on /compliance/complaints. Why do resolution outcome and complainant satisfaction differ?',
      'I am on /compliance/complaints. Why is Action required empty when complaints exist?',
      'I am on /compliance/complaints. Why does a resolved complaint show acknowledgement history?',
      'How do I record or correct complainant satisfaction?',
      'What should I do if a feedback lifecycle status update cannot be confirmed?',
      'How do I open a breach from another event?',
      'What needs to be communicated before I close a complaint?',
      "Why can't I close this complaint?",
      'How do I record that a complaint outcome was communicated?',
      'How do I generate a complaint outcome letter?',
      'Can I escalate a complaint to an external body?',
      "Why can't I mark a complaint improvement action completed?",
      'How do I share the public feedback link?',
      'Can someone submit feedback without choosing a service?',
      'How do I open feedback as a formal complaint?',
      'How do I review a custom complaint category for SIRS?',
      'I am on /compliance/complaints. Where are Acknowledge, Resolve and Escalate?', // Answer owner: complaints-and-whistleblower.
      'How do I return to my filtered complaints list and export records?', // Answer owner: complaints-and-whistleblower.
    ],
  },
  {
    prefix: '/compliance/continuous-improvement',
    questions: [
      'How do I complete an improvement action?',
      "Why can't I rate this improvement?",
      'How do I close an improvement?',
      'How do I approve an improvement plan?',
      'How do I produce our plan for continuous improvement?',
      'How do I suggest an improvement?',
      'Why does my improvement need a home?',
      'How do I mark improvements as reported to the board?',
      "Why didn't I get an overdue email?",

      'Why is adding an improvement action or RCA paused after an uncertain save?',
      'How do I choose an internal or external responsible person?',
      'What should I do when an improvement is saved but follow-up is unconfirmed?',
      'How do I select an incident, complaint or audit finding as an improvement source?',
      'How do I retry an unavailable improvement source search?',
      'How do I return to the exact source of an improvement?',
      'How do I view legacy audit plan evidence?',
      'Where do complaint improvement actions show up?',
      'How do I open the Continuous Improvement dashboard or My actions view?',
      'How do I choose a risk record as an improvement source?',
    ],
  },
  {
    prefix: '/compliance/standards',
    questions: [
      'Where do I confirm registration categories and Outcome 5.1 applicability?',
      'I am on /compliance/standards. How is my organisation access resolved?',
      'I am on /compliance/standards. Who can edit self-assessments?',
      'I am on /compliance/standards. Why can an outcome audit finding exist while the whole standard is not assessed?',

      'Why is Outcome 5.1 separate from the whole-standard rating?',
      'When is the RAC quality indicators and GPMS preparation panel shown?',
      'How do I record consumer engagement?',
      'How do I view standard-tagged evidence?',
      'Why are organisation-wide actions separate from this standard?',
      'Why are evidence mappings unavailable while other Standards data is visible?',
      'Why is New Assessment disabled?',
    ],
  },
  {
    prefix: '/compliance/quality-indicators',
    questions: [
      'How does pre-fill decide if a value can be used?',
      'How do I attest that our records are complete for a QI quarter?',
      'How do I review pre-filled falls in Quarter?',
      'When must I record psychosis for QI antipsychotics?',
      'Why are exports unavailable for an older reporting-period format?',
      'Why is a pre-filled value marked unknown?',
      'When does a QI quarter open automatically?',
      'How do I open a QI quarter for a home?',
      'How do I enter totals instead of resident-level data?',
      'How do I write the comment for weights or ADL assessments not recorded?',
      'Where can I check the collection rules for each QI?',
      'How do I create an improvement activity from a quality indicator?',
      'How do I record residents who were away for the whole quarter?',
      'What does the indicative Star Ratings Quality Measures card show?',
      'Who can see the restricted collection dates?',
      'How do I record a GPMS lodgement?',
      'How do I export the GPMS upload file?',
      'What if a field was not collected?',
      'How do I download the evidence register?',
      'Why do provider reporting facts require confirmation?',
      'Where do I enter Quality Indicator data?',
      'Who can enter and submit Quality Indicator data?',
      'How do I prepare an AIHW Quality Indicator submission?',
      'How do I record a residential home QI lodgement already made in GPMS?',
      'Does Statura transmit QI data to GPMS or AIHW?',
      'How do I correct a home quarter census?',
      'How are QCE-ACC and QOL-ACC scores recorded?',
      'Why is Quality Indicators evidence coverage unavailable?',
      'How do I import the Department recording templates in Quarter?',
      'How do I import a GPMS error file?',
      "What happens to residents that don't match?",
    ],
  },
  {
    prefix: '/dashboards/quality',
    questions: ['Where can I review RN coverage beside QI reporting?'],
  },
  {
    prefix: '/compliance/governance/risk-register',
    questions: [
      'Where does Add to risk register appear and what does it do?',
      'How do I open a saved risk?',
      'How is the risk rating calculated?',
      'What does the governance applicability banner mean?',
      'How do I choose a risk record as an improvement source?',
      'How do I record a risk review?',
      'How do I accept a risk?',
      'How do I add suggested risks?',
      'How do I close or reopen a risk?',
      'How do I export the risk register?',
    ],
  },
  {
    prefix: '/compliance/whistleblower',
    questions: [
      'I am on /compliance/whistleblower. How do I manage recurring obligations and evidence?',
      'I am on /compliance/whistleblower. Where do I edit the recurring obligations shown in Compliance?',
      'How do I track protected report remediation?',
      'What happens if no recipient is configured yet?',
      'How do I add a whistleblower recipient?',
      'Where do I open whistleblower reports and investigations?',
      'How does a care worker open the concern form?',
      "Can I type an investigator's name instead of picking them from a list?",
    ],
  },
  {
    prefix: '/dashboards/huddle',
    questions: ['What does Doses needing review (24h) count?'],
  },
  {
    prefix: '/medications/mar',
    questions: [
      'How do I record an S8 dose when the app cannot witness offline?',
      'What do Due, Overdue and Missed mean on the MAR?',
      'How do I review unscheduled medication orders?',
      'Why are pending and future orders placeholders on the MAR?',
      'How do I retry a failed MAR load or record?',
      'Where do I record the RN continuation wellbeing reason?',
      'How do I record a medication administration?',
      'Does opening a MAR record change its recorded time?',
      'How do I record an actual administration when timing is unconfirmed?',
      'What is the difference between refused and withheld?',
      'Why is Record Administration temporarily unavailable?',
      'How do I prepare a dose on my own signed-in device?',
      'How do I recover a pending prepared medication record?',
      'How do I review medication and regulatory reconciliation?',
    ],
  },
  {
    // Pending-delivery Help explains that missing clinical recipients require direct handover.
    prefix: '/clients/medications/monitoring',
    questions: [
      'How do I record an INR result and the next clinical response?',
      'Does a pending medicine follow-up entry mean the clinician received it?',
      'How do I end warfarin monitoring without creating another test?',
      'How do I correct a result recorded against a superseded monitoring plan?',
      'What happens to urgent INR results when monitoring is paused?',
    ],
  },
  {
    prefix: '/clients/medications',
    // Keep programme priorities within the seven visible client-chart prompts.
    // The programme question also covers verified Switch actions; general options are not client membership proof.
    // Remaining questions stay available as follow-ups and authored answers.
    byProgram: {
      RAC: [
        'Why is a verbal order awaiting confirmation?',
        'What happens when verbal-order documentation is overdue?',
        'How do I activate a medication with chart evidence?',
        'How do I cease or schedule the cease of a medication?',
        'Why does an active medication say Schedule not recognised?',
        'When does a new medication order need clinical review?',
        'How do I view saved prescription timing in the client chart?',
      ],
      SAH: [
        'How do I record community medication support without counting a dose twice?',
        'How do I view saved timing in the care worker app?',
        'How do I view saved prescription timing in the client chart?',
        'How do I confirm dose times for a prescription?',
      ],
    },
    questions: [
      'How do I record community medication support without counting a dose twice?',
      'Why does an active medication say Schedule not recognised?',
      'When does a new medication order need clinical review?',
      'What happens when verbal-order documentation is overdue?',
      'Why is a verbal order awaiting confirmation?',
      'How do I view saved prescription timing in the client chart?',
      'How do I confirm dose times for a prescription?',
      'How do I view saved timing in the care worker app?',
      'Why does the client medication chart show timing unconfirmed?',
      'How do I enter the observed onset date for an adverse drug reaction?',
      'How do I activate a medication with chart evidence?',
      'How do I cease or schedule the cease of a medication?',
      'How do I withdraw an order entered in error?',
      'How do I capture and confirm a verbal medication order?',
      'How do I attach handwritten prescriber follow-up?',
      'How do I adjudicate a quarantined dose as not given?',
      'Where do I review a saved medication overflow dose?',
      'What if medication capture says Not saved centrally?',
      'How do I confirm documented PRN limits?',
      'How do I record a PRN effectiveness follow-up?',
      'How does a colleague witness a prepared dose using their phone?',
      'How do I prepare a dose on my own signed-in device?',
      'How do I recover a pending prepared medication record?',
      'How do I review medication and regulatory reconciliation?',
      'How do I review retained medication exposure?',
      'What should I do if medication exposure review confirmation is pending?',
      'Does medication exposure review create an administration?',
      'Why could an allergy conflict prevent adding a medication?',
      'How do I classify a medication for the QI program?',
      "Why does Medications say it isn't turned on for my organisation?",
      'Why am I asked to choose a programme to view medications?',
    ],
  },
  {
    prefix: '/medications/monitoring/setup',
    questions: [
      'How do I enable or disable warfarin INR follow-through?',
      'What happens to urgent INR results when monitoring is paused?',
    ],
  },
  {
    prefix: '/medications/monitoring',
    questions: [
      'How do I record an INR result and the next clinical response?',
      'Does a pending medicine follow-up entry mean the clinician received it?',
      'How do I end warfarin monitoring without creating another test?',
      'How do I correct a result recorded against a superseded monitoring plan?',
      'What happens to urgent INR results when monitoring is paused?',
    ],
  },
  {
    prefix: '/medications',
    questions: [
      'Why does an active medication say Schedule not recognised?',
      'When does a new medication order need clinical review?',
      'What happens when verbal-order documentation is overdue?',
      'Why is a verbal order awaiting confirmation?',
      'How do I record a medication administration?',
      'What if medication capture says Not saved centrally?',
      // L4: this existing question now opens the RN review and receipt steps in managing-medications.
      'What if a medication dose is stuck in Offline & sync?',
      'How do I report a SIRS incident?',
      'Where do I find Journey?',
      'How are medication action badges labelled?',
      'Why could an allergy conflict prevent adding a medication?',
      "Why does Medications say it isn't turned on for my organisation?",
      'Why am I asked to choose a programme to view medications?',
    ],
  },
  {
    prefix: '/residential/activities',
    questions: ['How do I restrict visitors during an outbreak?'],
  },
  {
    prefix: '/residential/admissions',
    conditionalQuestions: [
      {
        questions: ['How do I check the agency source before editing imported admission details?'],
        requiresIntegration: 'dhda_b2g',
        requiresRoles: [
          'COORDINATOR',
          'COMPLIANCE_OFFICER',
          'QUALITY_OFFICER',
          'CASE_MANAGER',
          'REGISTERED_NURSE',
        ],
        requiredModules: ['/settings/integrations/dhda'],
      },
    ],
    questions: [
      'I am on /residential/admissions. How do I record the accommodation class and daily amount basis for a deposit?',
      'I am on /residential/admissions. The agreement class is missing. How do I record it?',
      'What should I do when another resident change is being saved?',
      'Why is the Agreement representative list empty?',
      'How do I record an existing resident’s opening bed?',
      'What should I do if the opening state records a different bed or room?',
      'How do I record election into 2025 arrangements?',
      'Can I set up a facility and register its first resident before funding is active?',
      'We only provide community care. How do we admit our first resident?',
      'How do I see today’s occupancy?',
      'How do I manage rooms and beds?',
      'How do I discharge a resident?',
      'How do I close outstanding work after a resident dies or is discharged?',
      'Can I review a planned resident before funding is active?',
    ],
  },
  {
    prefix: '/residential/dap-accrual',
    questions: ['What does DAP reconciliation do?'],
  },
  {
    // Answer owner: residential-evacuation-list; Confirmed conflict overrides, relevant-domain edits, RN review due and Review status unavailable.
    prefix: '/residential/evacuation-list',
    questions: [
      'How do I use the evacuation planning list?',
      'What does Review status unavailable mean on the evacuation list?',
    ],
  },
  {
    prefix: '/residential',
    questions: [
      'How do I see today’s occupancy?',
      'How do I track RAC service agreements and the 31 October 2026 variations?',
      'How do I manage rooms and beds?',
      'How do I discharge a resident?',
      'How do I close outstanding work after a resident dies or is discharged?',
      'What happens to a bed when a client is temporarily in hospital?',
    ],
  },
  {
    prefix: '/residential/care-minutes',
    questions: [
      'Why are care minutes unassessable in roster coverage or audit evidence?',
      'What happens if we fall below the care minute target?',
      'How do I track care minutes?',
      'How are mobile and residential care-minute targets graded?',
      'How are care minutes calculated from timesheets?',
      'How do I manage rooms and beds?',
      'How do I manage resident accommodation charges?',
    ],
  },
  {
    prefix: '/residential/daily-living',
    questions: [
      'Why does daily care show partial, refused or needs review?',
      'Daily living says it is taking longer than expected. What do I do?',
    ],
  },
  // The existing handover answer covers manual RN tasks while automation is OFF.
  {
    prefix: '/residential/meal-management',
    questions: [
      'Why must an RN publish the current mealtime instruction as ACTIVE before switching off?',
      'How do I publish or replace a current mealtime instruction?',
      'What do I do with stale, conflicting or offline mealtime instructions?',
      'Why is my mealtime screen showing the last confirmed instruction?',
      'When is the mealtime instruction linked to offline mobile ADL care?',
      'How do I record meal delivery and hand over a concern while automation is off?',
      'How do I enable restricted kitchen mealtime access?',
      'Why are meal totals or evidence unavailable?',
    ],
  },
  {
    prefix: '/residential/meal-management/setup',
    questions: [
      'Why must an RN publish the current mealtime instruction as ACTIVE before switching off?',
      'How do I publish or replace a current mealtime instruction?',
      'What do I do with stale, conflicting or offline mealtime instructions?',
      'Why is my mealtime screen showing the last confirmed instruction?',
      'How do I record meal delivery and hand over a concern while automation is off?',
      'How do I enable restricted kitchen mealtime access?',
      'Why are meal totals or evidence unavailable?',
    ],
  },
  {
    prefix: '/residential/occupancy',
    questions: [
      'What should I do when another resident change is being saved?',
      'How do I confirm leave for a billing month?',
      'How do I discharge a resident?',
      'Where do I record a resident’s discharge destination?',
      'How do I discharge a resident who has no bed recorded?',
      'What happens to a bed when a client is temporarily in hospital?',
      'How do I move a client to a different room?',
    ],
  },
  {
    prefix: '/residential/rooms',
    questions: [
      'Where do I link the opening bed for a resident already in care?',
      'What happens to a bed when a client is temporarily in hospital?',
      'How do I move a client to a different room?',
      'How do I manage rooms and beds?',
      'How do I track care minutes?',
      'How do I manage resident accommodation charges?',
    ],
  },
  {
    prefix: '/settings/billing',
    questions: [
      'What happens to compliance-only people after activation?',
      'How do I record an incident for a compliance-only person?',
      'How do I promote a compliance-only person to a full record?',
      'How are residential bed and active-client billing counts calculated?',
      'Why is Essentials checkout unavailable while pricing is being finalised?',
      'How do I choose Compliance Essentials (Home Care) without SAH operations?',
      'Why is subscription usage unavailable?',
      'I am on /settings/billing. Why is my Settings save refused after access changes?',
    ],
  },
  {
    prefix: '/finance/billing/readiness',
    questions: [
      'How do I check the period is ready to invoice?',
      'What does an advisory finding mean?',
    ],
  },
  {
    prefix: '/finance/billing',
    questions: [
      "How do I run this period's billing from check to push?",
      'Does pushing an invoice send it to the client or funder?',
      'A push failed — where do I see why?',
    ],
  },
  {
    prefix: '/finance/payers',
    questions: [
      'How do I configure brokerage invoice line and reference templates?',
      'How do I import head-provider client IDs for many Support at Home clients?',
      'How do I bill one invoice for a couple?',
      "Where do I set a payer's payment terms?",
      'A member account is missing from the payer — where do I add it?',
    ],
  },
  {
    prefix: '/finance/charges',
    questions: [
      'How do I add a client charge to an invoice?',
      "Why don't visit kilometres match the worker's timesheet kilometres?",
      'I need to correct a charge that is already on a sent invoice — how?',
    ],
  },
  {
    prefix: '/finance/statements',
    questions: ['Where do I see everything a payer owes across periods?'],
  },
  {
    // Issue client invoice shortcuts and failed-load retry are in the answer.
    prefix: '/finance/tac',
    questions: [
      'How do I issue a TAC invoice for one client?',
      'How do I record a partial TAC payment or denial?',
      'How do I respond to a TAC evidence request?',
    ],
  },
  {
    prefix: '/finance/brokerage-invoices',
    questions: [
      'How do I resolve a brokerage invoice template hold?',
      'How are brokered visits priced when we hold no care plan?',
      'How do I request a credit note?',
      'What does "Voided via credit note" mean on a row?',
      'When can I unapprove and reissue an invoice?',
    ],
  },
  {
    prefix: '/finance/invoices',
    chspQuestions: [
      'How do I settle, refund or match Xero payments for a CHSP invoice?',
      'Why is part of a CHSP invoice correction held for finance review?',
      'Which CHSP invoice details can regional finance readers see?',
      'Why is the CHSP finance panel unavailable on an invoice?',
    ],
    questions: [
      'Why can’t I see Bulk push to Xero or push an invoice?',
      'How do I fix a missing contact, account or tax mapping before pushing an invoice?',
      'How do I issue and reconcile NDIS claims and participant invoices?',
      'Where do I view an NDIS participant statement?',
      'How do I fix a manual invoice validation error?',
      'How do I cancel a draft invoice?',
      'How are invoice numbers assigned?',
      'Why were private services skipped when I generated invoices?',
      'How do I resolve a private quote, payer authority or GST hold?',
      'How do I combine two invoices into one?',
      'Why does this invoice differ from last week?',
      'How do I see the visit behind an invoice line?',
      'Why does this invoice show a push error?',
      'How do I return to my invoice filters after reviewing an invoice?',
      'What do invoices marked overdue represent?',
      'Why are programme invoice totals unavailable?',
      'How do I record separate payments with the same reference?',
    ],
  },
  {
    prefix: '/finance/respite-billing',
    questions: [
      'How do I review a respite episode and send its billable days to Xero?',
      'What should I do if a respite billing change cannot be saved?',
      'Which documents can I use as respite fee evidence?',
      'Can respite admission proceed if the document category is not fee evidence?',
    ],
  },
  {
    prefix: '/finance/an-acc',
    questions: [
      'How do I record historical daily eligible AN-ACC bed counts?',
      'How do I enter daily AN-ACC counts from a spreadsheet?',
      'Can I copy previous daily AN-ACC counts, and what evidence is needed?',
    ],
  },
  {
    // Header Review charges is reader-safe; Setup readiness is finance-only and Month close excludes oversight readers.
    prefix: '/finance/residential-billing',
    questions: [
      'How do I check which residents are ready to bill?',
      'How do I run residential billing for the month?',
      'How do I check a billing run adds up before finalising?',
      'What happens when I finalise or send a billing run to Xero?',
      'How do I print a billing statement for a resident?',
      'How do I correct a billing month already sent to Xero?',
      'How do I navigate residential billing and all-homes month close?',
      'Which run summaries can compliance and quality officers review?',
      'How do I close the billing month across all homes?',
      'How do I resolve a billing exception outside Statura or record a correction refund?',
      'What should I check at month end and year end for residential billing?',
      'How do I finalise without a leave confirmation?',
      // ACCHOLD: this answer covers missing accommodation and the Finalise majority acknowledgement.
      'How do I release billing holds for missing entry or discharge facts?',
      'How do I record a social-leave opening for a resident?',
      'How do I import accommodation opening balances?',
      'How do I approve or reject a stored fee import?',
      "Why can't I generate March?",
      "What does 'rates changed after billing' mean?",
      'How do I manage resident accommodation charges?',
      'How do I track care minutes?',
      'How do I manage rooms and beds?',
      // Pension guidance also explains the invoice payment instruction and saved export split.
      "How do I change a resident's pension deduction?",
      "How do I see each resident's Xero invoice status?",
      "How do I record a resident's Services Australia fee advice?",
      'How do I correct a fee advice?',
      "How do I send a resident's bills to their representative?",
      'How do I record GST treatment for a resident fee agreement?',
      "How do I record a resident's higher everyday living fee?",
      'How do I record a transitional higher everyday living fee?',
      'How do I record a bed reservation agreement?',
      'How do I suspend or vary a higher everyday living fee?',
      'How do I load fees for existing residents?',
      'How do I reopen a finalised residential billing run?',
      "Why wasn't a resident included in this month's billing run?",
      'How do I bill residents left off a finalised run?',
      'How do I prepare a final bill when a resident leaves or dies?',
      'How do I check a billing run adds up before finalising?',
      'What happens when I finalise or send a billing run to Xero?',
      'Why does a residential Xero send need finance review?',
      "What does the Why column in a resident's day rows mean?",
    ],
  },
  {
    prefix: '/finance/accommodation',
    questions: [
      'How is DAP calculated?',
      'Why is MPIR for my agreement date not loaded yet?',
      'How do I manage resident accommodation charges?',
      "Why can't I open a resident's journey from the accommodation queue?",
      'What does switching a resident between RAD and DAP change?',
      'How is a residential daily accommodation payment fixed on the price agreement day?',
    ],
  },
  {
    prefix: '/pricing/agreements',
    questions: [
      'How do I find and open a pending service agreement?',
      'Can a coordinator see client service agreements?',
      'How do I track, resend or re-issue signing requests?',
      'Why are my service agreement summaries partial?',
    ],
    chspQuestions: [
      'How do I complete a CHSP service agreement?',
      'Does a CHSP client have to sign before care can be delivered?',
      'How do I set CHSP provider agreement drafting defaults?',
    ],
  },
  {
    prefix: '/pricing/rate-cards',
    questions: [
      'How do I complete a Support at Home price publication review?',
      'Does Statura submit prices to My Aged Care?',
      'How do I keep rate cards current?',
      'Which timezone do rate card effective dates use?',
      'How do I activate a draft rate card?',
    ],
  },
  {
    prefix: '/telehealth',
    questions: [
      'How does a client join and leave a video visit?',
      'How is the telehealth session type labelled?',
    ],
  },
  {
    prefix: '/me/roster',
    questions: [
      ...CARE_WORKER_ROSTER_QUESTIONS,
      'What should I do when shift care instructions are cached or unavailable?',
      'How do I confirm attendance at a facility with a QR code?',
    ],
  },
  {
    // Added with the 2026-09-09 move from /availability. The lockstep rule wants
    // a path-first question for a renamed dashboard route, and this page now
    // shows two different kinds of record, so "why can I not change that one"
    // is the question it actually provokes.
    prefix: '/me/availability',
    questions: [
      'How do I say when I am unavailable?',
      'I am on /me/availability. How do I ask for a discussion about a refused flexible request?',
      'Why can I not change a record my coordinator added?',
      'Does my availability stop me being rostered?',
      'How does recurring monthly unavailability work?',
      'Why could My availability not load my worker profile?',
      'How do I ask for flexible working?',
      'What can I do if my flexible working request is refused?',
      'What should I do when shift care instructions are cached or unavailable?',
    ],
  },
  {
    prefix: '/me/hours',
    questions: [
      'How do I review my hours?',
      'How do I submit my timesheet?',
      'How do I request leave?',
    ],
  },
  {
    prefix: '/me/shift-offers',
    questions: [
      'How do I accept a shift offer?',
      'Why is an open-shift broadcast closed after I assign a worker?',
      'How do I view my roster?',
      'Are shift swaps enabled for my organisation?',
    ],
  },
  {
    prefix: '/me/shift-swaps',
    questions: [
      'Are shift swaps enabled for my organisation?',
      'How do I view my roster?',
      'How do I accept a shift offer?',
    ],
  },
  {
    prefix: '/me/renewals',
    questions: [
      // Answer owner: my-renewals; replacement evidence and independent review.
      'I am on /me/renewals. How do I submit a new police certificate without replacing my current clearance before review?',
      'How do I complete a renewal?',
      'Why is my screening renewal status unavailable?',
      'How do I view my credentials?',
      'How do I complete mandatory training?',
    ],
  },
  {
    prefix: '/me/profile',
    conditionalQuestions: [
      {
        questions: ['What credential level is recorded for a B2G submission?'],
        requiresIntegration: 'dhda_b2g',
        requiresRoles: [...B2G_SUBMIT_ROLES],
        requiredModules: ['/settings/integrations/dhda'],
      },
    ],
    questions: [
      'How do I update my profile?',
      'How do I set up two-step sign-in?',
      'How do I view my credentials?',
      'How do I update my work preferences?',
    ],
  },
  {
    prefix: '/me/compliance',
    questions: [
      'Why does My compliance show no linked worker record on Essentials?',
      'How do I view my compliance items?',
      'How do I complete a renewal?',
      'How do I view my credentials?',
    ],
  },
  {
    prefix: '/me/credentials',
    questions: [
      'How do I view my credentials?',
      'How do I complete a renewal?',
      'How do I complete mandatory training?',
    ],
  },
  {
    prefix: '/me/clinical-pin',
    questions: [
      'How do I set my clinical PIN?',
      'How do I record a medication administration?',
      'How do I view my credentials?',
    ],
  },
  {
    prefix: '/me/capability',
    questions: [
      'How do I view my capability record?',
      'How do I complete mandatory training?',
      'How do I view my credentials?',
    ],
  },
  {
    prefix: '/me/casual-conversion',
    questions: [
      'How do I request casual conversion?',
      'I am on /me/casual-conversion. Where is Employee choice and what if I disagree with a response?',
      'Where can I read the full written response to my employee choice request?',
      'Why is Employee choice unavailable?',
      'I am on /me/casual-conversion. Can I explain why I am raising a dispute?',
      'How do I raise a dispute about my employee choice request?',
      'How do I review my hours?',
      'How do I view my roster?',
    ],
  },
  {
    // Recruitment is a view of Workers; only recruitment roles reach it.
    prefix: '/people/workers',
    questions: [
      'How do I hire a candidate who accepted an offer?',
      'I am on /people/workers. How do I record the award level in an offer?',
    ],
    requiresRoles: [...RECRUITMENT_TENANT_WIDE_ROLES, ...RECRUITMENT_SCOPED_ROLES],
  },
  // The matching path-first answer covers candidate codes, saved signing status and replacement links.
  {
    prefix: '/people/recruitment',
    questions: [
      'How do I send an employment offer for electronic signature?',
      'I am on /people/workers. How do I check an offer signing request after a reload?',
    ],
    requiresRoles: [...RECRUITMENT_TENANT_WIDE_ROLES, 'HR_OFFICER'],
  },
  {
    prefix: '/people/workers',
    questions: [
      'How do I send an employment offer for electronic signature?',
      'I am on /people/workers. How do I check an offer signing request after a reload?',
    ],
    requiresRoles: [...RECRUITMENT_TENANT_WIDE_ROLES, 'HR_OFFICER'],
  },
  {
    prefix: '/people/evidence',
    questions: [
      'I am on /people/evidence. Why does the signed-document follow-up total show —?',
      'How do I export a large worker record completely?',
    ],
  },
  {
    prefix: '/people/development/supervision',
    questions: ['How do I record a clinical competency assessment?'],
  },
  {
    // Flexible working requests are a view of Development.
    prefix: '/people/development',
    questions: [
      'How do I respond to a flexible working request?',
      'I am on /people/development. What should I update after agreeing to flexible work?',
      'Why can’t I refuse a flexible working request?',
      'How do I record a flexible working request a worker emailed me?',
    ],
  },
  {
    prefix: '/people/development/casual-conversion',
    questions: [
      'How do I record the outcome of an employee choice dispute?',
      'Can I lodge a dispute with the Fair Work Commission from Statura?',
    ],
  },
  {
    prefix: '/people/development/performance',
    questions: ['Which timezone should I use when recording a manager 1:1?'],
  },
  {
    prefix: '/me/performance',
    questions: [
      'How do I view my performance record?',
      'How do I complete mandatory training?',
      'How do I view my credentials?',
    ],
  },
  {
    prefix: '/me/work-readiness',
    questions: [
      'How do I respond to my return-to-work plan?',
      'How do I withdraw a consent I gave?',
    ],
  },
  {
    prefix: '/me/policies',
    questions: [
      'I am on /me/policies. How do I read, acknowledge and continue to the next policy in the worker app?',
      'I am on /me/policies. How do I report possible client harm without copying a colleague’s identity?',
      'How do I acknowledge policies?',
      'How do I complete onboarding documents?',
      'How do I view my compliance items?',
      'Can I acknowledge a policy without waiting for a timer?',
      'How do I raise a concern about something at work?',
      'Can I report a concern without giving my name?',
    ],
  },
  {
    prefix: '/me/preferences',
    questions: [
      'How do I update my work preferences?',
      'How do I view my roster?',
      'How do I update my profile?',
      'How is my preferences mode labelled?',
    ],
  },
  {
    prefix: '/me/training/external-evidence/new',
    questions: [
      'I am on /me/training/external-evidence/new. What happens after I submit my certificate?',
      'How do I complete mandatory training?',
    ],
  },
  {
    prefix: '/me/training',
    questions: [
      'I am on /me/training. How do I submit an external certificate without choosing a course?',
      'What if training fails to open in the worker app?',
      'How do I complete mandatory training?',
      'How do I view my credentials?',
      'How do I complete a renewal?',
      'Does accepted prior learning count toward roster training requirements?',
    ],
  },
  {
    prefix: '/help/tickets',
    questions: [
      'What must I leave out of Help Chat, and where is my text processed?',
      'How do I use the Help Centre?',
      'How do I confirm a support ticket prepared by Help Chat?',
      'How do I approve timesheets?',
      'How do I report a SIRS incident?',
    ],
  },
  {
    prefix: '/help',
    questions: ['What must I leave out of Help Chat, and where is my text processed?'],
  },
  {
    prefix: '/settings/modules',
    questions: [
      'How do I find and enable Data Import?', // Answer owner: data-import.
      'Why does saving module preferences say Ask your organisation administrator?',
    ],
  },
  {
    prefix: '/settings/import',
    questions: [
      'How do I stage, approve and apply a Settings data import?',
      'Why could my import outcome not be confirmed?',
      'How does the system handle duplicates during import?',
      'How do I fix client import contact, phone or location errors?',
      'Why does an import control say Ask your organisation administrator?',
    ],
  },
  {
    prefix: '/feature-flags',
    questions: ['I am on /feature-flags. How do I change a flag for one organisation only?'],
  },
  {
    prefix: '/settings/feature-flags',
    questions: [
      'Why does a feature flag or rollout control say Ask your organisation administrator?',
    ],
  },
  {
    prefix: '/settings/funding-authorisations',
    questions: [
      'Why does a funding authorisation control say Ask your organisation administrator?',
    ],
  },
  {
    prefix: '/settings/notification-audit',
    questions: ['What does required incident event recovery mean?'], // Includes operator review and audited support resolution.
  },
  {
    prefix: '/settings/notifications',
    questions: [
      'Why does a notification template control say Ask your organisation administrator?',
    ],
  },
  {
    prefix: '/settings/webhooks/docs',
    questions: ['Are inbound APIs available for importing records?'],
  },
  {
    prefix: '/settings/webhooks',
    questions: [
      'Why does a webhook control say Ask your organisation administrator?',
      'Who can create, revoke or map a webhook key?',
    ],
  },
  {
    prefix: '/settings/people/assignment-requirements',
    questions: [
      'How do I use the composed worker requirements editor?',
      'Why is the requirement publications register unavailable?',
      'How do requirement stages set due dates?',
      'How do I publish rules and apply them to existing workers?',
      'Which rule version applies when worker details change?',
      'When are worker requirements assigned automatically?',
      'Does every mandatory library course get assigned automatically?',
      'How do I retry automatic worker assignments?',
      'Why must I preview the requirement again?',
      'How do I preview worker audiences and requirement stages?',
      'What does an unknown audience attribute mean?',
      'How do I configure worker onboarding requirements?',
      'I am on /settings/people/assignment-requirements. How do course team rules assign current and newly matching workers by role and site?',
      'Why does requirement preview or publication say Ask your organisation administrator?',
    ],
  },
  {
    prefix: '/settings/people',
    questions: [
      'I am on /settings/people. Why is a worker shown as Awaiting onboarding?',
      'I am on /settings/people. How do I manage whistleblower recurring obligations?',
      'How do I configure onboarding reminder settings?',
      'I am on /settings/people. How do I configure digests and chasing?',
      'How do I use the composed worker requirements editor?',
      'How do I require a manager override before a first shift?',
      'Can I use People setup tools when operational presentation is unavailable?',
      'I am on /settings/people. How do I change what happens when legal evidence is missing?',
      'I am on /settings/people. How do I manage screening and work-rights checks outside Statura?',
      'I am on /settings/people. How do I change learning renewal reminder windows without notifying the existing backlog?',
      'How are People notifications batched, and when are undated items chased?',
      'I am on /settings/people. How do I search for a worker and record a first-shift override?',
      'I am on /settings/people. Where do I edit whistleblower obligations and small-business status?',
      'I am on /settings/people. Why is my Settings save refused after access changes?',
      'Why can’t I save onboarding or learning reminder settings?',
      'Can I save reminder preferences without a tenant timezone?',
      'How do I opt out of onboarding reminder channels?',
    ],
  },
  {
    prefix: '/settings/family',
    questions: ['I am on /settings/family. Why is my Settings save refused after access changes?'],
  },
  {
    prefix: '/settings/onboarding/readiness',
    questions: ['I am on /settings/onboarding/readiness. What does this policy confirm?'],
  },
  {
    prefix: '/settings/onboarding',
    questions: [
      'I am on /settings/onboarding. Why is my Settings save refused after access changes?',
    ],
  },
  {
    prefix: '/settings',
    questions: [
      'I am on /settings. Where do I set up documents and e-signatures?',
      'I am on /settings. Why does document setup say Not checked yet?',
      'I am on /settings. Why does a document setup step say Ask your organisation administrator?',

      'Where do I check whether the Family app is enabled?',
      'How do I stop family members seeing health information?',
      'Where do I set the privacy notice version for the Family app?',
      'How long does a client’s agreement to share last?',
      'How do I open Settings navigation on a narrow screen?',
      'I am on /settings. Why is a link to another page missing?',
      'I am on /settings. Why does a Settings control say Ask your organisation administrator?',
      'How do I find a setting and return to my Settings search?',
    ],
  },
  {
    prefix: '/tasks/signatures',
    questions: [
      'Why does a signing document show unavailable?',
      'How do I track, resend or re-issue signing requests?',
      'When does a family signing link need reissue?',
    ],
  },
];

const PATH_LOOKUP_QUESTIONS = new Set([
  'help',
  'this page',
  'this screen',
  'help me',
  'what is this page',
  'what is this screen',
]);

export function helpChatRoleCohort(role: string | null | undefined): HelpChatRoleCohort {
  if (
    hasAnyRole(role, ['CARE_WORKER', 'AGENCY_CONTRACTOR', 'LIFESTYLE_OFFICER', 'ALLIED_HEALTH'])
  ) {
    return 'care_worker';
  }
  if (
    hasAnyRole(role, ['REGISTERED_NURSE', 'ENROLLED_NURSE', 'CLINICAL_LEAD', 'CLINICAL_DIRECTOR'])
  ) {
    return 'clinician';
  }
  if (
    hasAnyRole(role, [
      'COORDINATOR',
      'CASE_MANAGER',
      'TEAM_LEADER',
      'FACILITY_MANAGER',
      'TENANT_ADMIN',
      'SYSTEM_ADMIN',
      'OPERATIONS_DIRECTOR',
    ])
  ) {
    return 'coordinator';
  }
  return 'other';
}

export function isHelpPathLookupQuestion(question: string | null | undefined): boolean {
  if (!question) return false;
  const normalised = question
    .toLowerCase()
    .trim()
    .replace(/[?!.,'"]+$/g, '')
    .replace(/\s+/g, ' ');
  return PATH_LOOKUP_QUESTIONS.has(normalised);
}

function questionsForEntry(
  entry: PathSuggestion,
  role: string | null | undefined,
  providerType: ProviderType,
): string[] {
  const questions = baseQuestionsForEntry(entry, role, providerType);
  const roleQuestions = (entry.roleQuestions ?? [])
    .filter((group) => hasAnyRole(role, group.requiresRoles))
    .flatMap((group) => group.questions);
  // Preserve the primary question and its three default follow-ups while keeping
  // role-specific help within the six visible path suggestions.
  questions.splice(4, 0, ...roleQuestions);
  return questions;
}

function baseQuestionsForEntry(
  entry: PathSuggestion,
  role: string | null | undefined,
  providerType: ProviderType,
): string[] {
  const payerReferenceQuestion = 'Where do I record a head-provider client ID for Support at Home?';
  const payerReferenceQuestions =
    entry.prefix === '/clients' &&
    providerType === 'SAH' &&
    hasAnyRole(role, [
      'SYSTEM_ADMIN',
      'TENANT_ADMIN',
      'FACILITY_MANAGER',
      'COORDINATOR',
      'CASE_MANAGER',
      'FINANCE_OFFICER',
      'REGION_MANAGER',
      'REGION_FINANCE_OFFICER',
    ])
      ? [payerReferenceQuestion]
      : [];
  if (
    entry.prefix === '/payroll/readiness' &&
    !hasAnyRole(role, ['OWNER', 'TENANT_ADMIN', 'SYSTEM_ADMIN'])
  ) {
    const questions = entry.questions.filter(
      (question) =>
        question !==
        'In Payroll > Readiness, how does a tenant administrator enable leave correction writes?',
    );
    // Finance officers need the past-period repair entry within the six visible
    // suggestions; payroll officers retain leave setup and warning guidance.
    if (hasAnyRole(role, ['FINANCE_OFFICER', 'REGION_FINANCE_OFFICER'])) {
      const repair = 'How do I repair missing or outdated pay roles for a past pay period?';
      const index = questions.indexOf(repair);
      if (index >= 0) {
        questions.splice(index, 1);
        questions.splice(2, 0, repair);
      }
    }
    return questions;
  }
  const racAgreementQuestions =
    entry.prefix === '/clients' &&
    providerType === 'RAC' &&
    hasAnyRole(role, [
      'SYSTEM_ADMIN',
      'TENANT_ADMIN',
      'REGION_MANAGER',
      'FACILITY_MANAGER',
      'COORDINATOR',
      'CLINICAL_LEAD',
      'FINANCE_OFFICER',
      'REGION_FINANCE_OFFICER',
    ])
      ? [
          hasAnyRole(role, ['FINANCE_OFFICER', 'REGION_FINANCE_OFFICER'])
            ? 'Where can finance review RAC agreement evidence and refunds?'
            : 'How do I record RAC service agreement consent in Stay & agreements?',
        ]
      : [];
  if (entry.prefix === '/clients' && role === 'TENANT_ADMIN') {
    return [
      ...racAgreementQuestions,
      'How do I start a care plan?',
      ...payerReferenceQuestions,
      'How do I review a client’s role in compliance events?',
      'In Clients, how do I record a family communication decision?',
      'When does a family signing link need reissue?',
      'How do I administer family access for a client?',
      'How do I show a care team member to family?',
      'How do I publish a health summary or activity to family?',
      'How do I share a care plan or document with family?',
      'How do I record a queued or failed copy due to a registered supporter?',
      'When is a document recorded as issued to the client?',
      'How do I share a personal update with family?',
      'How do I assign a family form?',
      'How do I give a family member access to a client’s information?',
      'How do I record that a client withdrew consent for family to see their information?',
      'Where does Open agreement take an Intake client?',
      'How do I return to my search after opening a record?', // managing-client-records: Opening a record and returning.
    ];
  }
  const cohort = helpChatRoleCohort(role);
  return entry.byRole?.[cohort]
    ? [...racAgreementQuestions, ...payerReferenceQuestions, ...entry.byRole[cohort]]
    : [
        ...racAgreementQuestions,
        ...payerReferenceQuestions,
        ...(entry.byProgram?.[providerType] ?? []),
        ...entry.questions.filter(
          (question) =>
            question !== payerReferenceQuestion &&
            !(
              entry.prefix === '/clients' &&
              (question === 'Where can finance review RAC agreement evidence and refunds?' ||
                (hasAnyRole(role, ['FINANCE_OFFICER', 'REGION_FINANCE_OFFICER']) &&
                  question ===
                    'How do I record RAC service agreement consent in Stay & agreements?'))
            ),
        ),
      ];
}

function questionPrograms(question: string): readonly ('SAH' | 'RAC')[] {
  const programs: ('SAH' | 'RAC')[] = [];
  if (
    /\bSupport at Home\b|\bSAH\b|\bHome Care Operations\b|\bJourney\b|\bvisit pack\b|\bhome access\b|\bCM time\b|\bmissed visit\b/iu.test(
      question,
    )
  ) {
    programs.push('SAH');
  }
  if (
    /\bRAC\b|\bresidential\b|\bresident accommodation\b|\bcare minutes?\b|\brooms? and beds?\b|\boccupancy\b|\bfacility lead sidebar\b/iu.test(
      question,
    )
  ) {
    programs.push('RAC');
  }
  return programs;
}

export function isHelpQuestionVisibleForProvider(
  question: string,
  providerType: ProviderType,
  program?: HelpProgramTag | null,
): boolean {
  if (question === 'How do I record emergency evacuation assistance in a care plan?') {
    return providerType === 'RAC' && program !== 'CHSP';
  }
  if (/\bCHSP\b/iu.test(question)) return program === 'CHSP';
  const programs = questionPrograms(question);
  if (programs.length === 0) return true;
  if (programs.length > 1) return false;
  return providerType === programs[0];
}

/**
 * Route-policy visibility for a path entry. A prefix that is itself a routed
 * page keeps its own policy decision. A prefix naming a family of dynamic pages
 * (for example `/roster/replace` for `/roster/replace/[shift_id]`) has no page
 * of its own; help paths drop opaque identifiers, so the family's child route
 * policy decides instead.
 */
function isEntryRouteVisible(
  entry: PathSuggestion,
  providerType: ProviderType,
  providerProgramContext?: ProviderProgramContext,
): boolean {
  if (providerType === 'UNKNOWN') return true;
  const prefixHref = rewriteMvpPolishL1Href(entry.prefix);
  const provider = providerProgramContext ?? providerType;
  if (isHrefVisibleForProviderProgram(prefixHref, provider)) return true;
  if (!isDynamicPageFamily(entry)) return false;
  return isHrefVisibleForProviderProgram(`${prefixHref}/*`, provider);
}

function isDynamicPageFamily(entry: PathSuggestion): boolean {
  const prefixHref = rewriteMvpPolishL1Href(entry.prefix);
  return (
    !matchGeneratedProgramRoutePolicy('web', prefixHref) &&
    Boolean(matchGeneratedProgramRoutePolicy('web', `${prefixHref}/*`))
  );
}

function matchingPathEntry(
  currentPath: string | null | undefined,
  providerType: ProviderType = 'UNKNOWN',
  providerProgramContext?: ProviderProgramContext,
): PathSuggestion | null {
  const path = normalizeHelpChatPath(currentPath);
  if (!path) return null;

  const matches = PATH_SUGGESTIONS.filter(
    (entry) => path === entry.prefix || path.startsWith(`${entry.prefix}/`),
  ).sort((a, b) => b.prefix.length - a.prefix.length);

  // A dynamic-page family hidden for this provider falls back to its parent
  // entry, as it did before the family had its own questions.
  return (
    matches.find(
      (entry) =>
        !isDynamicPageFamily(entry) ||
        isEntryRouteVisible(entry, providerType, providerProgramContext),
    ) ?? null
  );
}

function suggestionsForPath(
  currentPath: string | null | undefined,
  role?: string | null,
  providerType: ProviderType = 'UNKNOWN',
  enabledModules?: string[] | null,
  governmentIntegrations?: GovernmentIntegrationEnablement,
  program?: HelpProgramTag | null,
  providerProgramContext?: ProviderProgramContext,
): string[] {
  const entry = matchingPathEntry(currentPath, providerType, providerProgramContext);
  if (!entry) return [];
  if (entry.requiresRoles && !hasAnyRole(role, [...entry.requiresRoles])) return [];
  if (entry.requiresIntegration && governmentIntegrations?.[entry.requiresIntegration] !== true)
    return [];
  if (
    entry.requiredModules &&
    (enabledModules === undefined ||
      (entry.requiredModules.some((path) => path.startsWith('/settings/integrations/')) &&
        !entry.requiredModules.every((path) => enabledModules?.includes(path) === true)) ||
      (enabledModules !== null &&
        !entry.requiredModules.every((modulePath) =>
          isModuleNavigationVisible(modulePath, enabledModules),
        )))
  ) {
    return [];
  }
  // A caller without a resolved provider lens may still receive genuinely
  // program-neutral path help. Question-level program classification below
  // remains the authority and removes SAH/RAC prompts. Once a provider/lens is
  // known, the executable route policy must also permit the path.
  if (!isEntryRouteVisible(entry, providerType, providerProgramContext)) {
    return [];
  }
  const conditional = (entry.conditionalQuestions ?? [])
    .filter(
      (group) =>
        governmentIntegrations?.[group.requiresIntegration] === true &&
        hasAnyRole(role, [...group.requiresRoles]) &&
        group.requiredModules.every((modulePath) => enabledModules?.includes(modulePath) === true),
    )
    .flatMap((group) => group.questions);
  const baseQuestions =
    entry.prefix === '/payroll/pay-runs' &&
    /\/push-to-xero$/.test(normalizeHelpChatPath(currentPath) ?? '')
      ? [
          'How do I write priced payslips to Xero?',
          'How do I reconcile payslips after posting in Xero?',
          'What does Resume write do on the payslip panel?',
          ...questionsForEntry(entry, role, providerType),
        ]
      : questionsForEntry(entry, role, providerType);
  return [
    ...(entry.conditionalQuestionsLast ? baseQuestions : conditional),
    ...(entry.conditionalQuestionsLast ? conditional : baseQuestions),
  ].filter(
    (question) =>
      isHelpQuestionVisibleForProvider(question, providerType, program) &&
      (!/\b(?:HI|IHI|HPI-I|HPI-O)\b|Healthcare Identifier|search sex|search demographics|Not set and Not stated/i.test(
        question,
      ) ||
        governmentIntegrations?.hi === true),
  );
}

function chspSuggestionsForPath(
  currentPath: string | null | undefined,
  role: string | null | undefined,
  program: HelpProgramTag | null | undefined,
  providerType: ProviderType,
  enabledModules?: string[] | null,
  governmentIntegrations?: GovernmentIntegrationEnablement,
  providerProgramContext?: ProviderProgramContext,
): string[] {
  if (program !== 'CHSP') return [];
  const entry = matchingPathEntry(currentPath, providerType, providerProgramContext);
  if (!entry) return [];
  if (entry.requiresRoles && !hasAnyRole(role, [...entry.requiresRoles])) return [];
  if (entry.requiresIntegration && governmentIntegrations?.[entry.requiresIntegration] !== true)
    return [];
  if (
    entry.requiredModules &&
    (enabledModules === undefined ||
      (entry.requiredModules.some((path) => path.startsWith('/settings/integrations/')) &&
        !entry.requiredModules.every((path) => enabledModules?.includes(path) === true)) ||
      (enabledModules !== null &&
        !entry.requiredModules.every((modulePath) =>
          isModuleNavigationVisible(modulePath, enabledModules),
        )))
  ) {
    return [];
  }
  if (!isEntryRouteVisible(entry, providerType, providerProgramContext)) {
    return [];
  }
  const questions = entry.chspQuestions ?? [];
  return questions.filter(
    (question) =>
      isHelpQuestionVisibleForProvider(question, providerType, program) &&
      (!/\b(?:HI|IHI|HPI-I|HPI-O)\b|Healthcare Identifier|search sex|search demographics|Not set and Not stated/i.test(
        question,
      ) ||
        governmentIntegrations?.hi === true),
  );
}

export function getHelpChatQuestionForPath(
  currentPath: string | null | undefined,
  role?: string | null,
  providerType: ProviderType = 'UNKNOWN',
  enabledModules?: string[] | null,
  governmentIntegrations?: GovernmentIntegrationEnablement,
  program?: HelpProgramTag | null,
  providerProgramContext?: ProviderProgramContext,
): string | null {
  const path = currentPath?.split('?')[0]?.replace(/\/$/, '');
  // Help Centre itself never auto-asks; its privacy question stays a suggestion.
  if (path === '/help') return null;
  if (path === '/roster/dispatch')
    return 'I am on /roster/dispatch. How do I use More, Exceptions and empty-week actions?';
  if (
    path === '/roster/leave' &&
    currentPath?.includes('request_id=') &&
    ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'PAYROLL_OFFICER', 'FINANCE_OFFICER'].includes(role ?? '')
  )
    return 'How do I confirm public holiday pay during leave?';
  if (path === '/roster/leave') return 'How do I follow up or dismiss a recorded absence?';
  const pathSuggestions = suggestionsForPath(
    currentPath,
    role,
    providerType,
    enabledModules,
    governmentIntegrations,
    program,
    providerProgramContext,
  );
  const chspSuggestions = chspSuggestionsForPath(
    currentPath,
    role,
    program,
    providerType,
    enabledModules,
    governmentIntegrations,
    providerProgramContext,
  );
  // A resolved CHSP-only workspace leads with its CHSP-specific operational
  // prompt. Every other caller, including one without the full context, keeps
  // the established path-first ordering.
  const chspOnly =
    program === 'CHSP' &&
    providerProgramContext?.activeProgramFamily === 'CHSP' &&
    providerProgramContext.eligibleProgramFamilies.length === 1;
  if (chspOnly && chspSuggestions[0]) return chspSuggestions[0];
  return pathSuggestions[0] ?? chspSuggestions[0] ?? null;
}

export function getHelpChatFollowUpQuestions(
  currentPath: string | null | undefined,
  role: string | null | undefined,
  askedQuestion: string | null | undefined,
  providerType: ProviderType = 'UNKNOWN',
  limit = 3,
  enabledModules?: string[] | null,
  governmentIntegrations?: GovernmentIntegrationEnablement,
  program?: HelpProgramTag | null,
  providerProgramContext?: ProviderProgramContext,
): string[] {
  const asked = askedQuestion?.trim() ?? '';
  const merged: string[] = [];
  // Path suggestions are already module-filtered for the page; only the generic prompts need
  // the module check (they are offered regardless of where the user is).
  const generic = [...COMMON_SUGGESTIONS, 'How do I use the Help Centre?'].filter((question) =>
    isSuggestionModuleVisible(question, enabledModules),
  );
  for (const question of [
    ...suggestionsForPath(
      currentPath,
      role,
      providerType,
      enabledModules,
      governmentIntegrations,
      program,
      providerProgramContext,
    ),
    ...chspSuggestionsForPath(
      currentPath,
      role,
      program,
      providerType,
      enabledModules,
      governmentIntegrations,
      providerProgramContext,
    ),
    ...generic,
  ]) {
    if (
      !question ||
      question === asked ||
      merged.includes(question) ||
      !isHelpQuestionVisibleForProvider(question, providerType, program)
    )
      continue;
    merged.push(question);
    if (merged.length >= limit) break;
  }
  return merged;
}

export function getHelpChatSuggestions(
  orgType: ModuleOrgType | null | undefined,
  currentPath?: string | null,
  role?: string | null,
  providerType: ProviderType = 'UNKNOWN',
  enabledModules?: string[] | null,
  governmentIntegrations?: GovernmentIntegrationEnablement,
  program?: HelpProgramTag | null,
  features?: HelpSuggestionFeatures,
  providerProgramContext?: ProviderProgramContext,
): string[] {
  void orgType;
  const common = [...COMMON_SUGGESTIONS];
  const org =
    providerType === 'SAH'
      ? [...HOME_CARE_SUGGESTIONS]
      : providerType === 'RAC'
        ? [...RESIDENTIAL_SUGGESTIONS]
        : ['How do I connect Xero payroll?', 'How do I use the Help Centre?'];

  const merged: string[] = [];
  const generic = [...common, ...org].filter((question) =>
    isSuggestionModuleVisible(question, enabledModules),
  );
  const pathQuestions = withCareTodaySuggestions(
    suggestionsForPath(
      currentPath,
      role,
      providerType,
      enabledModules,
      governmentIntegrations,
      program,
      providerProgramContext,
    ),
    matchingPathEntry(currentPath, providerType, providerProgramContext)?.prefix,
    features,
  );
  if (
    features?.payrollRebuildOn &&
    (currentPath === '/payroll' || currentPath?.startsWith('/payroll/'))
  ) {
    if (enabledModules && !isModuleNavigationVisible(currentPath, enabledModules)) return [];
    if (isPayrollStatusReviewer(role))
      return ['On /payroll, how do I check payroll status and fix held timesheets?'];
    return [
      'On /payroll, what do I do for the first pay run?',
      'On /payroll, how can one person approve pay and the Xero plan?',
      'On /payroll, how do I check rostered breaks?',
      'On /payroll, how do I fix held pay?',
      'On /payroll, how do I send and check payslips?',
      'On /payroll, how do I change a pay run?',
      'On /payroll, how do I return a period to the old payroll?',
      'On /payroll, where do I review time and pay setup?',
    ];
  }
  const entry = matchingPathEntry(currentPath, providerType, providerProgramContext);
  const normalizedPath = normalizeHelpChatPath(currentPath);
  if (
    features?.agencyAccess === true &&
    normalizedPath === '/head-office/agency-hours' &&
    entry?.prefix === '/head-office/agency-hours' &&
    isEntryRouteVisible(entry, providerType, providerProgramContext) &&
    enabledModules !== undefined &&
    isModuleNavigationVisible('/head-office/agency-hours', enabledModules) &&
    hasAnyRole(role, ['FINANCE_DIRECTOR'])
  ) {
    pathQuestions.unshift('How do finance directors review agency attendance?');
  }
  if (
    features?.agencyAccess === true &&
    entry?.prefix === '/payroll/agency-invoices' &&
    hasAnyRole(role, [...AGENCY_INVOICE_ALLOWED_ROLES]) &&
    pathQuestions.length > 0
  ) {
    // This existing feature-gated answer also covers saved reconciliation reviews.
    pathQuestions.unshift('How do I review agency attendance evidence for an invoice?');
  }
  if (
    features?.agencyAccess === true &&
    normalizedPath === '/roster/dispatch' &&
    hasAnyRole(role, [...AGENCY_IN_CHARGE_UI_ROLES]) &&
    pathQuestions.length > 0
  ) {
    pathQuestions.unshift('How do I set the nurse in charge for a shift?');
  }
  if (
    features?.stationPinSelfService === true &&
    entry?.prefix === '/admin/system/mobile-devices/shared-stations' &&
    pathQuestions.length > 0
  ) {
    pathQuestions.unshift(
      'How do I revoke a worker’s station PIN?',
      'What can a worker do if they forget their station PIN?',
      'How do staff change their station PIN?',
      'What should staff do if their station PIN may have been disclosed?',
      'How do I set a facility security contact for station PIN notices?',
    );
  }
  if (
    features?.agencyAccess === true &&
    entry?.prefix === '/people/workers' &&
    hasAnyRole(role, [...AGENCY_ATTESTATION_UI_ROLES]) &&
    pathQuestions.length > 0
  ) {
    pathQuestions.splice(
      1,
      0,
      'I am on /people/workers. How do I record and review an agency attestation?',
    );
  }
  if (
    features?.agencyAccess === true &&
    normalizedPath === '/roster/agency-review' &&
    hasAnyRole(role, [...AGENCY_REVIEW_UI_ROLES]) &&
    enabledModules !== undefined &&
    isModuleNavigationVisible('/roster', enabledModules) &&
    isHrefVisibleForProviderProgram('/roster/agency-review', providerProgramContext ?? providerType)
  ) {
    // The linked answer also explains retrying or stopping an uncertain review.
    pathQuestions.unshift('How do I review flagged agency work and pending identity records?');
  }
  // The worker profile publishes this capability only after both server-side
  // feature gates pass. Preserve all existing suggestions while it is absent.
  if (
    features?.stationPinManagerRevoke === true &&
    role === 'FACILITY_MANAGER' &&
    normalizeHelpChatPath(currentPath) === '/people/workers' &&
    entry?.prefix === '/people/workers' &&
    pathQuestions.length > 0
  ) {
    const question = 'How does a facility manager revoke a worker’s station PIN?';
    if (isHelpQuestionVisibleForProvider(question, providerType, program))
      pathQuestions.splice(4, 0, question);
  }
  const priority =
    features?.unifiedIncidentCapture === true
      ? entry?.prefix === '/compliance/incidents/new'
        ? [...ONE_DOOR_QUESTIONS]
        : ['/compliance/incidents', '/compliance/whs/incidents'].includes(entry?.prefix ?? '')
          ? [...ONE_DOOR_QUESTIONS, ...ONE_DOOR_OFFICE_QUESTIONS]
          : []
      : [];
  // Promote only questions admitted by the existing path/provider/module gates.
  // The remaining questions retain their original relative order.
  for (const question of [
    ...priority.filter((question) => pathQuestions.includes(question)),
    ...pathQuestions,
    ...generic,
    ...chspSuggestionsForPath(
      currentPath,
      role,
      program,
      providerType,
      enabledModules,
      governmentIntegrations,
      providerProgramContext,
    ),
  ]) {
    if (!isHelpQuestionVisibleForProvider(question, providerType, program)) continue;
    if (!merged.includes(question)) merged.push(question);
  }
  return merged.slice(0, currentPath?.startsWith('/clients/') ? 7 : 6);
}
